// AIChat.js - AI Chat dengan MarkdownRenderer, Export PDF, Token detail & history persistence
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  X, Send, Settings, Trash2, Bot, Copy,
  Check, Loader2, Sparkles, BarChart2, Database, FileDown,
  Clock, Target, Zap, History, Key, Maximize2, Minimize2, Pencil,
  FileText, ExternalLink, Table2, ThumbsUp, ThumbsDown,
  PictureInPicture2, PanelRight, Plus, RotateCcw,
} from 'lucide-react';
import { AI_PROVIDERS, loadConfig, getDefaultConfig, getKeyBudgets, loadDashboardConfig, saveDashboardConfig, CODE_KEY } from './aiConfig';
import { useDashboardData, formatDataForAI } from '../../context/DashboardDataContext';
import { api, useAuth } from '../../context/AuthContext';
import { MarkdownRenderer } from './MarkdownRenderer';
import { openNoteRef, gotoNote, isNoteSource, dashboardTargetOf, gotoDashboard } from './noteNav';
import { exportChatToPDF } from './ExportPDF';
import { exportChatToXLSX, exportChatToJSON } from './ExportChat';
import AISettings from './AISettings';
import { useLang } from '../../context/LanguageContext';
import { dashboardKeyForPath } from '../../config/dashboardKeys';
import {
  hasPageContextOf, isUnservablePage, needsAutoDetect, resolveModelId,
  buildModePayload, buildAskBody, normalizeUsage, answerModeOf, dataSourceLabel,
} from './aiAsk';
import { useChatImages } from './useChatImages';
import { useSlashCommands } from './useSlashCommands';
import { ChatImageStrip, ChatTextFileStrip, ChatAttachButton } from './ChatImageAttach';
import { useChatTextFiles, TEXT_MAX_BYTES } from './useChatTextFiles';
import ImageLightbox from './ImageLightbox';
import ToolTrace from './ToolTrace';
import SaveToMemoryButton from './SaveToMemoryButton';

// ── [AI Service] Integrasi ke AI Service (shared, Model A) ───────────────────
// Semua pertanyaan dikirim sebagai SATU panggilan ke /api/ai/ask; AI Service
// yang mengorkestrasi text-to-SQL + jawaban. Chart & pass-2 lokal tidak dipakai.
//
// Dulu ada saklar `USE_AI_SERVICE` dengan jalur lama (pass-1 → run-sql → pass-2
// → chart) sebagai jalan mundur. Saklar itu dipaku ke `true` dan jalur lamanya
// tidak pernah lagi tereksekusi, jadi 233 baris tsb dibuang: kode yang tak
// pernah dijalankan tidak pernah diuji, dan nilainya sebagai jalan mundur nol
// sementara ongkos bacanya nyata. Riwayatnya ada di Git bila perlu dilihat.

// ── [Mode Coding] Key semu untuk target "Coding" di AI Workspace ─────────────
// BUKAN dashboard data: tidak ada tabel/skema di baliknya. Dipakai sebagai
// dashboardKey agar system prompt & quick prompts punya scope sendiri di
// ai_dashboard_config, dan sebagai pemicu mode:'code' ke AI Service.
// Diekspor supaya AIWorkspace memakai konstanta yang SAMA (hindari string
// literal ganda yang bisa tidak sinkron).
// Sumbernya kini aiConfig.js (lihat komentar di sana: menghindari impor
// melingkar dengan AISettings). Di-export ulang di sini supaya pemanggil lama
// seperti AICoding.js tidak perlu diubah.
export { CODE_KEY };

// ── Konteks dashboard aktif (berbasis dashboardKey dari ROUTES) ───────────────
// Hint khusus untuk dashboard yang punya dataset. Halaman lain (admin/overview)
// memakai label rute sebagai nama + hint generik.
const DASH_HINTS = {
  aws_billing:    { name: 'AWS Billing Dashboard', hint: 'AWS Billing Dashboard — monitoring biaya dan penggunaan layanan AWS.' },
  aws_cost:       { name: 'AWS Cost Explorer',     hint: 'AWS Cost Explorer — analisis biaya AWS per Business Unit, service, dan tren bulanan.' },
  aws_cloudtrail: { name: 'AWS CloudTrail',        hint: 'AWS CloudTrail — log aktivitas/event akun AWS lintas region (siapa melakukan apa, kapan, dari IP mana, termasuk error).' },
  aws_inventory:  { name: 'AWS Inventory',         hint: 'AWS Inventory — snapshot jumlah resource AWS per service dan region.' },
  solman:         { name: 'SAP Solution Manager',  hint: 'SAP Solution Manager — monitoring tiket dan insiden SAP.' },
  sap_usage:      { name: 'SAP User Utilization',  hint: 'SAP User Utilization — analisis penggunaan user SAP.' },
  employee_overtime: { name: 'Employee Overtime',  hint: 'Employee Overtime — data lembur karyawan per Business Unit, Department, Section: jam lembur (OT_Hours), total jam (Total_OT_Hours), nilai lembur (OT_Amount), alasan, dan tanggal tugas.' },
};

// Di-export supaya AIPanel bisa menghitung NAMA dashboard yang identik untuk
// memfilter sidebar riwayat. Nama inilah — bukan dashboardKey — yang ditulis ke
// kolom `dashboard` di ai_chat_history saat pesan disimpan. Menyalin DASH_HINTS
// ke AIPanel akan membuat dua sumber kebenaran yang diam-diam bisa menyimpang,
// dan gejalanya cuma "sidebar kosong padahal riwayat ada".
export function getDashboardContext(dashboardKey, label) {
  if (dashboardKey && DASH_HINTS[dashboardKey]) return DASH_HINTS[dashboardKey];
  const name = label || 'Primary Dashboard';
  return { name, hint: `Halaman "${name}" di Primary Dashboard.` };
}

// Nama KANONIK untuk mode "Umum" di AIWorkspace (target tanpa dashboardKey).
// WAJIB literal statis, JANGAN t('aiw.general'): nilai ini ditulis ke kolom
// `dashboard` di ai_chat_history dan dipakai untuk memfilter sidebar riwayat.
// Label terjemahan membuat satu mode yang sama tersimpan sebagai nilai berbeda
// per locale ('Umum (tanpa data dashboard)' vs 'General (no dashboard data)'),
// sehingga riwayatnya terbelah mengikuti bahasa UI — bukan mengikuti isinya.
// Berbahasa Inggris agar konsisten dengan isi kolom lainnya (nama produk:
// 'AWS CloudTrail', 'SAP Solution Manager') dan dengan literal 'Coding'.
export const GENERAL_STORAGE_LABEL = 'General';

// Quick prompts di-key per dashboardKey. Varian "<key>.data" dipakai bila ada
// data nyata; varian dasar untuk fallback; "default" untuk halaman tanpa preset.
const QUICK_PROMPTS = {
  id: {
    // [MR-PG-AI] Source Management Reports — diuji 2026-10-16, jawaban & SQL tervalidasi.
    'mr_maintenance': [
      'Berapa total biaya maintenance bulan ini dibanding budgetnya?',
      'Bandingkan aktual vs budget maintenance per bulan selama 2026, tampilkan grafiknya',
      'Bulan apa saja yang biaya maintenance-nya melebihi budget?',
      'Departemen mana yang paling boros biaya maintenance tahun ini?',
      'Plant mana yang biaya maintenance-nya paling tinggi?',
    ],
    'mr_production': [
      'Jam mesin per work center bulan lalu untuk plant 1401',
      'Total downtime per kategori tahun ini',
      'Mesin mana dengan downtime terbanyak bulan lalu?',
      'Tren jam mesin per bulan tahun 2026 per plant, tampilkan grafiknya',
      'Ada konfirmasi jam mesin yang tidak wajar (melebihi jam kalender) tahun ini?',
    ],
    'mr_process': [
      'Konsumsi CPO per plant bulan lalu (MT)',
      'Produksi produk utama per bulan tahun 2026 untuk plant 1701',
      'Penerimaan CPO per supplier bulan ini (dengan nama supplier)',
      'Tren konsumsi CPO per bulan tahun 2026, tampilkan grafiknya',
    ],
    'mr_inventory': [
      'Berapa total nilai stok saat ini per company?',
      'Tampilkan 10 material dengan nilai stok terbesar saat ini',
      'Berapa nilai stok slow moving dan dead stock per kategori?',
      'Material apa yang paling banyak dipakai (konsumsi) bulan lalu?',
    ],
    'sap_usage.data': [
      'Siapa 5 user dengan usage tertinggi?',
      'Company mana yang paling banyak menggunakan SAP?',
      'Berapa user yang tidak aktif? Tampilkan dalam tabel',
      'Buat tabel perbandingan usage per modul',
    ],
    'solman.data': [
      'Rangkum tiket open yang perlu perhatian segera',
      'Buat tabel distribusi tiket per team',
      'Team mana yang paling banyak tiket open?',
      'Berapa close rate bulan ini vs bulan lalu?',
    ],
    'aws_cost.data': [
      'Business unit mana yang paling boros?',
      'Buat tabel breakdown biaya per BU',
      'AWS service apa yang paling mahal?',
      'Analisis tren biaya 6 bulan terakhir',
    ],
    'aws_billing.data': [
      'Service AWS mana yang paling mahal?',
      'Buat tabel breakdown biaya per service',
      'Berapa total tax yang dibayar?',
      'Analisis tren biaya per periode',
    ],
    'm365.data': [
      'Berapa total user, dan berapa yang berlisensi vs unlicensed?',
      'Top 10 department berdasarkan jumlah user berlisensi',
      'Buat tabel distribusi lisensi per domain',
      'Lisensi (SKU) apa yang paling banyak dipakai?',
    ],
    aws_billing:    ['Analisis tren biaya AWS', 'Service mana yang paling boros?', 'Rekomendasi penghematan biaya'],
    aws_cost:       ['Jelaskan anomali biaya', 'Forecast biaya bulan depan', 'Buat tabel perbandingan biaya bulanan'],
    m365:           ['Ringkas kondisi lisensi Microsoft 365 saat ini', 'Berapa user aktif vs nonaktif?', 'Buat tabel breakdown user per company'],
    aws_cloudtrail: ['Aktivitas apa yang paling sering terjadi?', 'Siapa user paling aktif?', 'Tampilkan event yang gagal (error)'],
    aws_inventory:  ['Service mana dengan resource terbanyak?', 'Buat tabel resource per region', 'Ringkas inventory AWS saat ini'],
    solman:         ['Rangkum tiket high priority', 'Pola insiden yang sering muncul', 'SLA yang berisiko breach'],
    sap_customizing: ['Daftar 10 TR SPRO terbaru', 'TR SPRO yang sudah di Q01 tapi belum di P01', 'Enhancement aktif per modul'],
    employee_overtime: ['Departemen mana dengan total jam lembur tertinggi?', 'Buat tabel 10 karyawan dengan lembur terbanyak', 'Berapa total nilai lembur per Business Unit?'],
    'aws_cloudtrail.data': [
      'Rangkum aktivitas CloudTrail 24 jam terakhir',
      'Event apa saja yang gagal (error/AccessDenied)? Tampilkan tabel',
      'Siapa 10 user/role paling aktif dan dari IP mana?',
      'Ada aksi sensitif (Delete*, Stop*, IAM, Security Group) hari ini?',
      'Ada login console dari IP atau region yang tidak biasa?',
    ],
    solman_docs:    ['Dokumen project apa saja yang terbaru?', 'Buat tabel jumlah dokumen per project', 'Cari dokumen terkait modul tertentu (mis. MM)'],
    sap_roles:      ['Role mana yang paling banyak di-assign ke user?', 'User mana yang punya role kritikal (mis. SAP_ALL)?', 'Tampilkan role yang tidak dipakai user mana pun'],
    sap_basis_mon:  ['Ringkas kondisi sistem SAP saat ini', 'Ada dump (ST22) atau job gagal hari ini?', 'Apa yang perlu ditindaklanjuti tim Basis sekarang?'],
    ai_history:     ['Pertanyaan apa yang paling sering ditanyakan ke AI?', 'Dashboard mana yang paling sering memakai AI?', 'Tampilkan jawaban AI yang gagal/error minggu ini'],
    notes:          ['Ringkas catatan terbaru', 'Cari catatan tentang topik tertentu', 'Buat daftar action item dari catatan'],
    sdp: [
      'Ringkas kondisi tiket SDP minggu ini: tiket baru, selesai, dan yang masih open',
      'Berapa tiket yang masih open per teknisi? Tampilkan tabel',
      'Tiket mana yang overdue atau paling lama belum selesai?',
      'Kategori masalah apa yang paling sering muncul bulan ini?',
      'Bandingkan jumlah tiket per company per bulan tahun ini, tampilkan grafik',
      'Siapa requester yang paling sering membuat tiket?',
      'Berapa rata-rata waktu penyelesaian tiket per teknisi?',
      'Buat tabel jumlah aset per company dan product type',
      'Aset mana yang gagal audit atau garansinya sudah/hampir habis?',
      'Berapa workstation per company dan berapa yang RAM-nya di bawah 8 GB?',
    ],
    netmap:         ['Jelaskan topologi jaringan di halaman ini', 'Link/perangkat mana yang sedang down atau sibuk?', 'Interface mana dengan utilisasi tertinggi?'],
    '__code__': [
      'Explain the architecture of this project root',
      'Find where the login JWT is created',
      'Show the pm2 error logs for dashboard and summarise',
      'Make a bar chart of files per folder in backend',
    ],
    default:        ['Jelaskan halaman ini', 'Apa yang harus diperhatikan?', 'Berikan ringkasan kondisi sistem'],
  },
  en: {
    'mr_maintenance': [
      'What is the total maintenance cost this month versus budget?',
      'Compare actual vs budget maintenance cost per month in 2026, show a chart',
      'Which months had maintenance cost above budget?',
      'Which department has the highest maintenance cost this year?',
      'Which plant has the highest maintenance cost?',
    ],
    'mr_production': [
      'Machine hours per work center last month for plant 1401',
      'Total downtime per category this year',
      'Which machines had the most downtime last month?',
      'Monthly machine hours trend in 2026 per plant, show a chart',
      'Are there any abnormal machine-hour confirmations (above calendar hours) this year?',
    ],
    'mr_process': [
      'CPO consumption per plant last month (MT)',
      'Main product output per month in 2026 for plant 1701',
      'CPO receipts per supplier this month',
      'Monthly CPO consumption trend in 2026, show a chart',
    ],
    'mr_inventory': [
      'What is the total current stock value per company?',
      'Show the 10 materials with the highest current stock value',
      'What is the slow moving and dead stock value per category?',
      'Which materials were consumed the most last month?',
    ],
    'sap_usage.data': [
      'Who are the top 5 users by usage?',
      'Which company uses SAP the most?',
      'How many users are inactive? Show in a table',
      'Create a usage comparison table per module',
    ],
    'solman.data': [
      'Summarize open tickets needing immediate attention',
      'Create a ticket distribution table per team',
      'Which team has the most open tickets?',
      'What is the close rate this month vs last month?',
    ],
    'aws_cost.data': [
      'Which business unit spends the most?',
      'Create a cost breakdown table per BU',
      'Which AWS service is the most expensive?',
      'Analyze the cost trend over the last 6 months',
    ],
    'aws_billing.data': [
      'Which AWS service is the most expensive?',
      'Create a cost breakdown table per service',
      'What is the total tax paid?',
      'Analyze the cost trend per period',
    ],
    'm365.data': [
      'How many users are there, and how many are licensed vs unlicensed?',
      'Top 10 departments by number of licensed users',
      'Create a license distribution table per domain',
      'Which license (SKU) is used the most?',
    ],
    aws_billing:    ['Analyze AWS cost trends', 'Which service is the most wasteful?', 'Cost-saving recommendations'],
    aws_cost:       ['Explain cost anomalies', 'Forecast next month cost', 'Create a monthly cost comparison table'],
    m365:           ['Summarize the current Microsoft 365 license status', 'How many users are active vs inactive?', 'Create a user breakdown table per company'],
    aws_cloudtrail: ['What activity happens most often?', 'Who is the most active user?', 'Show failed events (errors)'],
    aws_inventory:  ['Which service has the most resources?', 'Create a resource table per region', 'Summarize current AWS inventory'],
    solman:         ['Summarize high priority tickets', 'Recurring incident patterns', 'SLAs at risk of breach'],
    sap_customizing: ['List the 10 latest SPRO TRs', 'SPRO TRs already in Q01 but not yet in P01', 'Active enhancements per module'],
    employee_overtime: ['Which department has the highest total overtime hours?', 'Create a table of the top 10 employees by overtime', 'What is the total overtime amount per Business Unit?'],
    'aws_cloudtrail.data': [
      'Summarize CloudTrail activity in the last 24 hours',
      'Which events failed (error/AccessDenied)? Show a table',
      'Who are the 10 most active users/roles and from which IPs?',
      'Any sensitive actions today (Delete*, Stop*, IAM, Security Group)?',
      'Any console logins from unusual IPs or regions?',
    ],
    solman_docs:    ['Which project documents are the latest?', 'Create a table of document count per project', 'Find documents for a specific module (e.g. MM)'],
    sap_roles:      ['Which roles are assigned to the most users?', 'Which users hold critical roles (e.g. SAP_ALL)?', 'Show roles not assigned to any user'],
    sap_basis_mon:  ['Summarize the current SAP system status', 'Any dumps (ST22) or failed jobs today?', 'What should the Basis team follow up on now?'],
    ai_history:     ['What are the most frequently asked AI questions?', 'Which dashboard uses the AI most?', 'Show failed AI answers this week'],
    notes:          ['Summarize the latest notes', 'Find notes about a specific topic', 'List action items from the notes'],
    sdp: [
      'Summarize SDP tickets this week: new, resolved, and still open',
      'How many tickets are still open per technician? Show a table',
      'Which tickets are overdue or have been open the longest?',
      'Which issue categories appear most often this month?',
      'Compare ticket count per company per month this year, show a chart',
      'Which requesters create the most tickets?',
      'What is the average resolution time per technician?',
      'Build a table of asset count per company and product type',
      'Which assets failed audit or have warranty expired / expiring soon?',
      'How many workstations per company, and how many have less than 8 GB RAM?',
    ],
    netmap:         ['Explain the network topology on this page', 'Which links/devices are down or busy?', 'Which interfaces have the highest utilization?'],
    '__code__': [
      'Explain the architecture of this project root',
      'Find where the login JWT is created',
      'Show the pm2 error logs for dashboard and summarise',
      'Make a bar chart of files per folder in backend',
    ],
    default:        ['Explain this page', 'What should I pay attention to?', 'Give a system status summary'],
  },
};

function getQuickPrompts(dashboardKey, hasData, lang = 'id') {
  const set = QUICK_PROMPTS[lang] || QUICK_PROMPTS.id;
  if (hasData && dashboardKey && set[`${dashboardKey}.data`]) return set[`${dashboardKey}.data`];
  if (dashboardKey && set[dashboardKey]) return set[dashboardKey];
  return set.default;
}

function getDashboardKey(pathname) {
  return dashboardKeyForPath(pathname);
}

function buildDataContext(pathname, dashboardData) {
  if (!dashboardData) return null;
  try {
    return formatDataForAI(dashboardData);
  } catch (e) {
    console.error('[AIChat] buildDataContext error:', e);
    return null;
  }
}

// ── Session ID generator ─────────────────────────────────────────────────────
function genSessionId() {
  return 'sess_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}

// ── Save chat history ke backend (dengan retry 1x) ───────────────────────────
// Mengembalikan id baris ai_chat_history (atau null) — dipakai tombol 👍/👎.
async function saveChatMessage(payload, retry = true) {
  try {
    const res = await api.post('/api/ai/chat-history', payload);
    return res?.data?.id ?? null;
  } catch (e) {
    // Retry sekali setelah 1 detik (handle transient network issues)
    if (retry) {
      await new Promise(r => setTimeout(r, 1000));
      return saveChatMessage(payload, false);
    }
    console.warn('[AIChat] Save history failed (after retry):', e.message);
    return null;
  }
}

// ── [REVIEW] Feedback 👍/👎 per jawaban ──────────────────────────────────────
// Sinyal utama antrian review (backend/aiReview.js). 👎 membuka kotak koreksi
// opsional: "jawaban seharusnya" adalah bahan paling berharga untuk few-shot &
// regression test di tahap berikutnya. Teks UI berbahasa Inggris dan sengaja
// tidak lewat translations.js (gerbang paritas i18n check-i18n.js).
function FeedbackButtons({ msg }) {
  const [rating, setRating] = useState(msg.myRating || 0);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  if (!msg.id) return null;
  const send = async (r, correction) => {
    setBusy(true);
    try {
      await api.post('/api/ai/chat-feedback', { chat_id: msg.id, rating: r, correction: correction || null });
      setRating(r);
      return true;
    } catch (e) {
      console.warn('[AIChat] feedback failed:', e.message);
      return false;
    } finally { setBusy(false); }
  };
  const up = () => { setOpen(false); setSent(false); send(rating === 1 ? 0 : 1); };
  const down = async () => {
    if (rating === -1) { setOpen(false); setSent(false); send(0); return; }
    if (await send(-1)) { setSent(false); setOpen(true); }
  };
  const submit = async () => { if (await send(-1, text.trim())) { setSent(true); setOpen(false); } };
  const cls = (active, color) =>
    `flex items-center text-xs transition-colors disabled:opacity-50 ${active ? color : 'text-slate-400 hover:text-slate-600'}`;
  return (
    <>
      <button onClick={up} disabled={busy} title="Good answer" className={cls(rating === 1, 'text-emerald-600')}>
        <ThumbsUp style={{ width: 12, height: 12 }} fill={rating === 1 ? 'currentColor' : 'none'} />
      </button>
      <button onClick={down} disabled={busy} title="Wrong or unhelpful answer" className={cls(rating === -1, 'text-red-500')}>
        <ThumbsDown style={{ width: 12, height: 12 }} fill={rating === -1 ? 'currentColor' : 'none'} />
      </button>
      {sent && rating === -1 && <span className="text-[10px] text-slate-400">Thanks, queued for review</span>}
      {open && (
        <div className="basis-full mt-1 bg-red-50/60 border border-red-100 rounded-xl p-2">
          <p className="text-[11px] text-slate-600 mb-1">
            What should the correct answer be? <span className="text-slate-400">(optional)</span>
          </p>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
            placeholder="e.g. the correct figure, the right table or filter, or what was missing"
            className="w-full text-xs border border-slate-200 rounded-lg p-2 bg-white focus:outline-none focus:ring-1 focus:ring-brand-400" />
          <div className="flex justify-end gap-2 mt-1">
            <button onClick={() => { setOpen(false); setSent(true); }}
              className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1">Skip</button>
            <button onClick={submit} disabled={busy || !text.trim()}
              className="text-xs bg-brand-600 hover:bg-brand-700 text-white rounded-lg px-3 py-1 disabled:opacity-50">Send</button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Chip MODE jawaban ────────────────────────────────────────────────────────
// Menunjukkan bagaimana sebuah jawaban dihasilkan, supaya jelas kapan konteks
// percakapan terpakai dan kapan pertanyaan diperlakukan sebagai query baru.
const MODE_CHIP = {
  sql:           { labelKey: 'ai.modeSql',      titleKey: 'ai.modeSqlTitle',      bg: '#eff6ff', fg: '#1d4ed8', bd: '#bfdbfe' },
  followup:      { labelKey: 'ai.modeFollowup', titleKey: 'ai.modeFollowupTitle', bg: '#f5f3ff', fg: '#6d28d9', bd: '#ddd6fe' },
  context:       { labelKey: 'ai.modeContext',  titleKey: 'ai.modeContextTitle',  bg: '#fff7ed', fg: '#c2410c', bd: '#fed7aa' },
  deterministic: { labelKey: 'ai.modeDet',      titleKey: 'ai.modeDetTitle',      bg: '#ecfdf5', fg: '#047857', bd: '#a7f3d0' },
};

// ── Accuracy Score Calculator ─────────────────────────────────────────────────
function calcAccuracyScore(content, hasRealData) {
  if (!content) return null;
  let score = 50;
  if (hasRealData) score += 25;

  const numbers   = (content.match(/\d+[\.,]\d+/g) || []).length;
  const currency  = (content.match(/\$[\d,\.]+/g) || []).length;
  const pct       = (content.match(/\d+[\.,]?\d*\s*%/g) || []).length;
  const tables    = (content.match(/\|.+\|/g) || []).length;
  const bullets   = (content.match(/^[-•*]\s/gm) || []).length;

  score += Math.min(numbers  * 2, 10);
  score += Math.min(currency * 3, 10);
  score += Math.min(pct      * 2,  8);
  score += Math.min(tables   * 2,  8);
  score += Math.min(bullets  * 1,  5);

  if (content.length < 100) score -= 10;
  if (content.length > 500) score += 5;

  const hallucination = ['saya tidak memiliki akses','tidak tersedia','tidak dapat mengakses',
    'perlu informasi lebih','tidak bisa melihat','tidak memiliki data',
    'not provided','cannot specify','cannot determine','not available','do not have access',
    "don't have access",'unable to','need more information','cannot access','no data available'];
  hallucination.forEach(h => { if (content.toLowerCase().includes(h)) score -= 8; });

  return Math.min(100, Math.max(10, Math.round(score)));
}

// ── KB/RAG Score — heuristik untuk jawaban berbasis dokumen ──
function calcKbScore(content, sources) {
  if (!content) return null;
  let score = 60;
  if (/\[\d+\]/.test(content)) score += 15;
  if (Array.isArray(sources) && sources.length > 0) score += 10;
  if (content.length > 200) score += 5;
  if (/(^|\n)\s*[-•*]\s/.test(content) || /\*\*[^*]+\*\*/.test(content)) score += 5;
  const dunno = ['tidak tahu','tidak ada di dokumen','tidak ditemukan dalam dokumen',"don't know",'not in the documents','no relevant'];
  if (dunno.some(d => content.toLowerCase().includes(d))) score -= 20;
  return Math.min(100, Math.max(10, Math.round(score)));
}
function AccuracyBadge({ score }) {
  const { t } = useLang();
  if (!score) return null;
  const color = score >= 85 ? '#16a34a' : score >= 65 ? '#d97706' : '#dc2626';
  const bg    = score >= 85 ? '#f0fdf4' : score >= 65 ? '#fffbeb' : '#fef2f2';
  const label = score >= 85 ? t('ai.accuracyHigh') : score >= 65 ? t('ai.accuracyMed') : t('ai.accuracyLow');
  return (
    <span style={{ background: bg, color, border: `1px solid ${color}30`,
      borderRadius: 6, padding: '1px 6px', fontSize: 10, fontWeight: 700,
      display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      <Target style={{ width: 9, height: 9 }} />
      {t('ai.accuracy')} {score}% · {label}
    </span>
  );
}

function ResponseTimeBadge({ ms }) {
  if (!ms) return null;
  const sec   = (ms / 1000).toFixed(1);
  const color = ms < 3000 ? '#16a34a' : ms < 8000 ? '#d97706' : '#dc2626';
  const bg    = ms < 3000 ? '#f0fdf4' : ms < 8000 ? '#fffbeb' : '#fef2f2';
  return (
    <span style={{ background: bg, color, border: `1px solid ${color}30`,
      borderRadius: 6, padding: '1px 6px', fontSize: 10, fontWeight: 700,
      display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      <Clock style={{ width: 9, height: 9 }} />
      {sec}s
    </span>
  );
}

// ── Token Detail Badge ───────────────────────────────────────────────────────
function TokenBadge({ promptTokens, completionTokens, totalTokens, estimated }) {
  if (!totalTokens) return null;
  return (
    <span
      title={`Prompt: ${promptTokens} · Completion: ${completionTokens} · Total: ${totalTokens}${estimated ? ' (estimasi)' : ''}`}
      style={{ background:'#eff6ff', color:'#1d4ed8', border:'1px solid #bfdbfe',
        borderRadius: 6, padding: '1px 6px', fontSize: 10, fontWeight: 700,
        display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      <Zap style={{ width: 9, height: 9 }} />
      {promptTokens}↑ {completionTokens}↓ = {totalTokens}{estimated ? '~' : ''} tok
    </span>
  );
}

// ── DataTable — render baris hasil SQL sebagai tabel asli (akurat, anti-duplikat).
// Kolom diambil dari union key seluruh baris. Angka di-rata-kanan & diformat ringan.
export function DataTable({ rows, animateIn = false }) {
  const [grouped, setGrouped] = useState(true);
  const { t, lang } = useLang();
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const cols = [];
  const seen = new Set();
  for (const r of rows) {
    for (const k of Object.keys(r || {})) { if (!seen.has(k)) { seen.add(k); cols.push(k); } }
  }
  if (cols.length === 0) return null;

  // Label header ramah-baca. PENTING: deteksi format ($/angka) tetap memakai KEY
  // asli (colIsNum/currencyOf di bawah) — ini hanya mengubah TEKS yang tampil.
  // Kolom biaya AWS MoM dikirim backend sbg `usd_prev__<label>` / `usd_curr__<label>`
  // (tetap mengandung "usd" agar format $ bertahan); di sini prefix-nya dibuang.
  const prettyHeader = (c) => {
    const m = /^usd_(?:prev|curr)__(.+)$/.exec(c);
    if (m) return m[1];
    const isEN = lang === 'en';
    const mapId = {
      bu: 'BU', service: 'Service', usage: 'Usage',
      delta_abs: 'Selisih', calendar_effect: 'Efek Hari', real_change: 'Perubahan Asli',
      usd_prev: 'Sebelumnya', usd_curr: 'Bulan Ini',
    };
    const mapEn = {
      bu: 'BU', service: 'Service', usage: 'Usage',
      delta_abs: 'Difference', calendar_effect: 'Day Effect', real_change: 'Real Change',
      usd_prev: 'Previous', usd_curr: 'Current',
    };
    return (isEN ? mapEn : mapId)[c] || c;
  };

  const MAX = 200; // jaga performa render bila baris sangat banyak
  let shown = rows.slice(0, MAX);

  // Kolom tanggal SAP format YYYYMMDD (mis. released_on = '20261001') dulu
  // terdeteksi numerik -> tampil "20,261,001" dan ikut dijumlah di subtotal grup.
  // Deteksi: SEMUA sel non-kosong 8 digit dgn bulan/tanggal valid -> ubah ke
  // 'YYYY-MM-DD' (teks) sebelum deteksi numerik; tidak dipakai sbg kolom grup.
  const isYmd = (v) => {
    const s = String(v ?? '').trim();
    const m = /^((?:19|20)\d{2})(\d{2})(\d{2})$/.exec(s);
    return !!m && +m[2] >= 1 && +m[2] <= 12 && +m[3] >= 1 && +m[3] <= 31;
  };
  const colDate = {};
  for (const c of cols) {
    let any = false, all = true;
    for (const r of shown) {
      const v = r?.[c];
      if (v == null || v === '') continue;
      any = true;
      if (!isYmd(v)) { all = false; break; }
    }
    colDate[c] = any && all;
  }
  if (cols.some(c => colDate[c])) {
    shown = shown.map(r => {
      const o = { ...r };
      for (const c of cols) if (colDate[c] && o[c] != null && o[c] !== '') {
        const s = String(o[c]).trim();
        o[c] = `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
      }
      return o;
    });
  }

  // Nilai dianggap numerik bila number, atau string angka murni (boleh koma ribuan).
  const cellIsNum = (v) =>
    typeof v === 'number' ||
    (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v.replace(/,/g, ''))));

  // Numerik PER-KOLOM: kolom numerik hanya bila SEMUA sel non-kosong numerik.
  // Header & sel kolom numerik sama-sama rata-kanan → sejajar (tidak "renggang").
  const colIsNum = {};
  for (const c of cols) {
    let any = false, allNum = true;
    for (const r of shown) {
      const v = r?.[c];
      if (v == null || v === '') continue;
      any = true;
      if (!cellIsNum(v)) { allNum = false; break; }
    }
    colIsNum[c] = any && allNum;
  }

  // Deteksi mata uang PER KOLOM berdasarkan nama:
  // - IDR (Rp, tanpa desimal, titik ribuan) untuk kolom amount overtime.
  // - USD ($, 2 desimal) untuk kolom biaya AWS.
  // - selain itu: angka biasa. Kolom non-uang (hours/qty/year/month) tak tersentuh.
  const currencyOf = (col) => {
    const c = String(col).toLowerCase();
    if (/(^|_)(ot_amount|total_amount|amount)$/.test(c) || c === 'amount') return 'IDR';
    if (/usd|(^|_)cost($|_)|total_usd/.test(c)) return 'USD';
    return null;
  };
  const colCurrency = {};
  for (const c of cols) colCurrency[c] = colIsNum[c] ? currencyOf(c) : null;

  // Kolom "identitas numerik" (tahun/bulan/hari/id/nomor) — tampilkan angka apa
  // adanya TANPA pemisah ribuan (2026, bukan 2,026).
  const isPlainNumCol = (col) => {
    const c = String(col).toLowerCase();
    return /(^|_)(year|month|month_num|day|id|no|num|nomor|urut)$/.test(c) || c === 'year' || c === 'month' || c === 'day'
      // [MR-PG-AI] kode SAP (company/plant/periode/order/material) — bukan angka untuk dijumlah
      || /^(bukrs|werks|bwkey|periode|aufnr|matnr|lfgja|lfmon|tahun|bulan|mjahr|kode_material|material_code|company_code|plant_code)$/.test(c);
  };
  const colPlain = {};
  for (const c of cols) colPlain[c] = colIsNum[c] && !colCurrency[c] && isPlainNumCol(c);

  // ── Grouping bertingkat (Opsi B) ──────────────────────────────────────────
  // Kolom "penjumlahan": numerik & BUKAN identitas (year/month/id/no) → subtotal
  // masuk akal. Kolom identitas dilewati (menjumlah tahun/id menyesatkan).
  const colAdditive = {};
  for (const c of cols) colAdditive[c] = colIsNum[c] && !isPlainNumCol(c);
  // Kolom grup = kolom PALING KIRI yang non-numerik & punya nilai berulang.
  // Bila tak ada (semua unik / kolom kiri numerik) → tabel tetap datar.
  let groupCol = null;
  if (cols.length >= 2 && shown.length > 1) {
    for (const c of cols) {
      if (colIsNum[c] || colDate[c]) continue;
      // kolom tanggal ISO (YYYY-MM-DD...) juga bukan kolom grup yang berguna
      if (shown.every(r => r?.[c] == null || r[c] === '' || /^\d{4}-\d{2}-\d{2}/.test(String(r[c])))) continue;
      const distinct = new Set(shown.map(r => String(r?.[c] ?? '')));
      if (distinct.size >= 1 && distinct.size < shown.length) { groupCol = c; break; }
    }
  }
  const canGroup = !!groupCol;
  const useGroup = canGroup && grouped;
  // Susun grup STABLE (urut kemunculan pertama; urutan dalam grup dipertahankan).
  const toNum = (v) => { if (v == null || v === '') return null; const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, '')); return isFinite(n) ? n : null; };
  let groups = [];
  if (useGroup) {
    const gmap = new Map();
    for (const r of shown) {
      const key = String(r?.[groupCol] ?? '');
      let g = gmap.get(key);
      if (!g) { g = { key, rows: [], sub: {} }; gmap.set(key, g); groups.push(g); }
      g.rows.push(r);
    }
    for (const g of groups) {
      for (const c of cols) {
        if (!colAdditive[c]) continue;
        let sum = 0, any = false;
        for (const r of g.rows) { const n = toNum(r?.[c]); if (n != null) { sum += n; any = true; } }
        g.sub[c] = any ? sum : null;
      }
    }
  }

  // Format angka: currency IDR/USD bila kolomnya mata uang; kolom identitas =
  // angka polos tanpa pemisah; selain itu en-US biasa.
  const fmt = (v, numeric, currency, plain) => {
    if (v == null) return '';
    if (!numeric) return String(v);
    const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
    if (!isFinite(n)) return String(v);
    if (currency === 'IDR')
      return 'Rp ' + Math.round(n).toLocaleString('id-ID');       // Rp 345.759 (bulat, titik ribuan)
    if (currency === 'USD')
      return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (plain) return String(n);                                   // 2026, bukan 2,026
    return Number.isInteger(n)
      ? n.toLocaleString('en-US')
      : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Stagger reveal: penghitung baris-visual (naik terus di grup+anggota+flat).
  const STAGGER = 55, STAGGER_MAX = 30; // ms/baris, jumlah baris yg distagger
  let _vrow = 0;
  const mergeRv = (style) => {
    if (!animateIn) return { style };
    const d = Math.min(_vrow, STAGGER_MAX) * STAGGER; _vrow++;
    return { className: 'ai-rv-row', style: { ...style, animationDelay: d + 'ms' } };
  };

  return (
      <div className="mt-3" style={{ overflowX: 'auto' }}>
      {/* Reveal cascade: baris muncul berurutan (fade + naik) saat tabel pertama
          tampil, meniru gaya Claude "menulis" tabel. Hanya utk jawaban terbaru
          (animateIn) → riwayat lama tak berkedip. Distagger dgn animation-delay;
          hanya ~28 baris pertama distagger agar tabel panjang tak lama. */}
      {canGroup && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
          <button onClick={() => setGrouped(g => !g)}
            style={{ fontSize: 11, color: '#6366f1', background: 'transparent', border: '1px solid #e0e7ff', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}>
            {useGroup ? t('ai.grpFlat') : t('ai.grpBy') + ' ' + groupCol}
          </button>
        </div>
      )}
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5, border: '1px solid #e2e8f0' }}>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            {cols.map((c) => (
              <th key={c} style={{ textAlign: colIsNum[c] ? 'right' : 'left', padding: '7px 12px', fontWeight: 700, color: '#0f172a', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>{prettyHeader(c)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {useGroup
            ? groups.map((g, gi) => (
              <React.Fragment key={gi}>
                {/* Header grup: nama grup + subtotal kolom penjumlahan */}
                <tr {...mergeRv({ background: '#eef2ff' })}>
                  {cols.map((c) => {
                    if (c === groupCol) return (
                      <td key={c} style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 700, color: '#3730a3', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                        {g.key || t('ai.grpEmpty')} <span style={{ fontWeight: 400, color: '#818cf8', fontSize: 11 }}>· {g.rows.length}</span>
                      </td>
                    );
                    const hasSub = colAdditive[c] && g.sub[c] != null;
                    return (
                      <td key={c} style={{ textAlign: colIsNum[c] ? 'right' : 'left', padding: '6px 12px', fontWeight: 700, color: '#3730a3', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                        {hasSub ? fmt(g.sub[c], true, colCurrency[c], colPlain[c]) : ''}
                      </td>
                    );
                  })}
                </tr>
                {/* Baris anggota: kolom grup dikosongkan (indent) */}
                {g.rows.map((r, ri) => (
                  <tr key={ri} {...mergeRv({ background: ri % 2 ? '#f8fafc' : '#fff' })}>
                    {cols.map((c) => (
                      <td key={c} style={{ textAlign: colIsNum[c] ? 'right' : 'left', padding: '6px 12px 6px ' + (c === groupCol ? '28px' : '12px'), color: '#334155', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>
                        {c === groupCol ? '' : fmt(r?.[c], colIsNum[c], colCurrency[c], colPlain[c])}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))
            : shown.map((r, ri) => (
              <tr key={ri} {...mergeRv({ background: ri % 2 ? '#f8fafc' : '#fff' })}>
                {cols.map((c) => (
                  <td key={c} style={{ textAlign: colIsNum[c] ? 'right' : 'left', padding: '6px 12px', color: '#334155', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{fmt(r?.[c], colIsNum[c], colCurrency[c], colPlain[c])}</td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
      {rows.length > MAX && (
        <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Menampilkan {MAX} dari {rows.length} baris.</p>
      )}
    </div>
  );
}

// ── Message Component ─────────────────────────────────────────────────────────
// ── Stream halus (efek ketik ala Claude) ────────────────────────────────────
// Perbaikan vs implementasi lama (Opsi C):
//   • Reveal berbasis requestAnimationFrame + waktu (bukan setInterval + step
//     besar). Tiap frame hanya menambah sedikit karakter → aliran terasa mulus,
//     tak "melompat" pada jawaban panjang.
//   • Markdown dirender PROGRESIF: blok yang sudah lengkap langsung tampil rapi
//     (bold, list, TABEL, chart) sambil di-fade-in; hanya baris yang sedang
//     diketik yang tampil sebagai teks polos + caret. Jadi tabel muncul baris
//     demi baris, bukan "pop" sekaligus di akhir.
const TYPEWRITER_ENABLED = true;   // set false untuk mematikan efek stream
const REVEAL_CPS         = 240;    // kecepatan reveal (karakter / detik)
const REVEAL_MIN_MS      = 320;    // durasi minimal (biar jawaban pendek tetap terasa mengalir)
const REVEAL_MAX_MS      = 3400;   // durasi maksimal (jawaban panjang tak bikin nunggu lama)
const TYPEWRITER_MIN_LEN = 40;     // pesan lebih pendek → tampil langsung (tak dianimasi)

// Pisah teks-terungkap menjadi bagian "committed" (blok lengkap → boleh dirender
// markdown penuh) dan "tail" (baris berjalan → teks polos + caret). Blok kode /
// chart yang fence-nya belum tertutup DISEMBUNYIKAN sampai lengkap, agar JSON /
// kode tak terlihat mentah separuh.
function splitStream(revealed) {
  let s = String(revealed || '');
  // Di dalam fence ``` yang belum tertutup → potong sebelum fence pembuka.
  const fences = (s.match(/```/g) || []).length;
  if (fences % 2 === 1) { const f = s.lastIndexOf('```'); if (f >= 0) s = s.slice(0, f); }
  // Commit s/d newline terakhir; sisanya (baris berjalan) jadi tail.
  const nl = s.lastIndexOf('\n');
  if (nl >= 0) return { committed: s.slice(0, nl), tail: s.slice(nl + 1) };
  return { committed: '', tail: s };
}

// Render assistant secara progresif: committed (markdown penuh, di-memo agar
// hanya di-parse ulang saat ada blok baru selesai) + tail (teks berjalan) + caret.
function StreamingMarkdown({ text, revealLen, animating, sources }) {
  const shown = animating ? String(text || '').slice(0, revealLen) : String(text || '');
  const { committed, tail } = animating ? splitStream(shown) : { committed: shown, tail: '' };
  const committedEl = useMemo(
    () => (committed ? <MarkdownRenderer text={committed} sources={sources} animate={animating} /> : null),
    [committed, sources, animating]
  );
  return (
    <div>
      {committedEl}
      {animating && (
        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap mb-0">
          {tail}
          <span className="ai-caret" />
        </p>
      )}
    </div>
  );
}

// [PROGRESS] Fase mentah dari server → kalimat yang bisa dibaca.
//
// Teksnya TIDAK lewat translations.js: gerbang check-i18n.js menuntut paritas
// id/en/ar untuk setiap key, sementara ini label diagnostik pendek yang
// pembacanya admin. Pola yang sama dipakai AIHistorySidebar dan ToolTrace.
//
// Fase yang tidak dikenal ditampilkan APA ADANYA, bukan dijatuhkan ke teks
// generik: bila suatu saat ada fase baru di server yang lupa dipetakan di sini,
// lebih baik terlihat sebagai kata asing daripada tersamar jadi "Menganalisis"
// dan menyembunyikan bahwa ada tahap yang tak terpantau.
const PHASE_ID = {
  prepare:   'Menyiapkan permintaan',
  model:     'Menunggu model',
  tool:      'Menjalankan',
  sqlgen:    'Menyusun SQL',
  query:     'Menjalankan query',
  summarize: 'Merangkum hasil',
  retrieve:  'Mencari di knowledge base',
};

function phaseLabel(p, t) {
  if (!p || !p.phase) return t('ai.analyzing');
  const base = PHASE_ID[p.phase] || p.phase;
  const step = (p.step && p.total) ? ` ${p.step}/${p.total}` : '';
  const detail = p.detail ? ` · ${p.detail}` : '';
  return `${base}${step}${detail}`;
}

/**
 * Teks pesan USER yang melipat sendiri bila terlalu tinggi.
 *
 * DUA hal diperbaiki di sini, dan yang kedua sebenarnya lebih besar dari yang
 * diminta:
 *
 * 1. LIPATAN. Keluaran terminal yang di-paste bisa memenuhi seluruh layar dan
 *    mendorong jawaban keluar dari pandangan. Dibatasi COLLAPSED_MAX_PX dengan
 *    gradasi memudar di tepi bawah + tombol buka/tutup.
 *
 *    Ambangnya TINGGI PIKSEL, bukan jumlah baris. Alasannya persis kasus yang
 *    memicu permintaan ini: keluaran `node ... MODULE_NOT_FOUND` adalah satu
 *    baris logis yang membungkus jadi belasan baris visual. Ambang berbasis
 *    `\n` akan menyebutnya "pendek" dan tidak melipat apa pun.
 *
 * 2. JEDA BARIS YANG SELAMA INI HILANG. Render lama memakai `<p>` polos tanpa
 *    `whitespace-pre-wrap`, sehingga SETIAP newline di pesan user diruntuhkan
 *    jadi spasi oleh HTML. Log berbaris-baris yang di-paste tampil sebagai satu
 *    gumpalan — itulah sebab utama gambar di laporan tampak tak terbaca, bukan
 *    panjangnya. `whitespace-pre-wrap` mengembalikannya.
 *
 *    Font mono SENGAJA TIDAK dipakai: komponen Message ini dipakai bersama oleh
 *    AICoding, AIWorkspace, AIPanel, dan AIChatHistory, jadi menggantinya akan
 *    mengubah tampilan SEMUA pertanyaan biasa demi sebagian kecil yang berupa
 *    log. `break-words` tetap dipasang supaya path panjang tanpa spasi tidak
 *    menembus lebar gelembung.
 */
const COLLAPSED_MAX_PX = 160;

function CollapsibleUserText({ text }) {
  const { t } = useLang();
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // `t()` memulangkan KUNCI-nya sendiri bila terjemahan tidak ada
  // (`LanguageContext.js:44`), bukan undefined. Jadi pola `t(k) || 'fallback'`
  // TIDAK pernah jatuh ke fallback — ia menampilkan teks harfiah "ai.showMore"
  // di layar. Dibandingkan terhadap kuncinya supaya tombol ini tetap terbaca
  // sebelum kunci terjemahannya ditambahkan.
  const tr = (key, fallback) => { const s = t(key); return (s === key || s == null) ? fallback : s; };

  // Diukur SETELAH render, karena tinggi teks yang membungkus hanya diketahui
  // browser. Diukur ulang saat `text` berubah (mis. pesan yang diedit).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // scrollHeight dibaca saat elemen sedang dibatasi; +2px toleransi supaya
    // teks yang pas-pasan tidak mendapat tombol yang tak berguna.
    setOverflows(el.scrollHeight > COLLAPSED_MAX_PX + 2);
  }, [text]);

  return (
    <div>
      <div className="relative">
        <p
          ref={ref}
          className="text-sm leading-relaxed whitespace-pre-wrap break-words"
          style={(!expanded && overflows) ? { maxHeight: COLLAPSED_MAX_PX, overflow: 'hidden' } : undefined}
        >
          {text}
        </p>
        {/* Gradasi memudar. Warnanya WAJIB mengikuti `--brand-600`, bukan nilai
            biru yang ditulis mati: gelembung memakai `bg-brand-600`, dan warna
            itu berasal dari CSS variable di `index.css` yang berbeda per
            dashboard — panel Coding hijau, bukan biru. Gradasi yang dipatok
            akan tampak sebagai kotak salah warna di sebagian besar halaman.
            `pointer-events-none` wajib — tanpa itu ia menutupi teks di
            bawahnya dan blok tidak bisa diseleksi untuk disalin. */}
        {!expanded && overflows && (
          <div
            className="absolute inset-x-0 bottom-0 pointer-events-none rounded-b-2xl"
            style={{
              height: 44,
              background: 'linear-gradient(to bottom, rgb(var(--brand-600) / 0), rgb(var(--brand-600) / 0.95))',
            }}
          />
        )}
      </div>
      {overflows && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="mt-1.5 text-[11px] font-medium text-white/80 hover:text-white underline underline-offset-2"
        >
          {expanded ? tr('ai.showLess', 'Show less') : tr('ai.showMore', 'Show more')}
        </button>
      )}
    </div>
  );
}

// ── [FOLLOWUPS] Saran pertanyaan lanjutan (font kecil) di bawah jawaban terakhir.
// Cache per isi jawaban agar re-render / pindah tab tidak memanggil ulang.
const _followupCache = new Map();
function FollowUps({ question, answer, lang, onPick, dashboard }) {
  const key = (question || '') + '\u0000' + (answer || '').slice(0, 500);
  const [items, setItems] = useState(() => _followupCache.get(key) || null);
  useEffect(() => {
    if (_followupCache.has(key)) { setItems(_followupCache.get(key)); return; }
    let alive = true;
    api.post('/api/ai/followups', { question, answer, lang, dashboard: dashboard || '' })
      .then(r => {
        const f = (r && r.data && Array.isArray(r.data.followups)) ? r.data.followups : [];
        _followupCache.set(key, f);
        if (alive) setItems(f);
      })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [key]);
  if (!items || !items.length) return null;
  return (
    <div className="ml-9 -mt-1 mb-3 flex flex-col items-start gap-1">
      {items.map((q, i) => (
        <button key={i} onClick={() => onPick(q)}
          className="text-left text-[11px] leading-snug text-slate-500 hover:text-brand-700 hover:bg-brand-50 border border-slate-200 hover:border-brand-300 rounded-lg px-2 py-1 transition-colors">
          ↳ {q}
        </button>
      ))}
    </div>
  );
}

function Message({ msg, hasRealData, isLast, onStartEdit, hideAccuracy, animate, onAnimationDone, onOpenImage }) {
  const { t, lang } = useLang();
  const [copied, setCopied] = useState(false);
  const isAssistant = msg.role === 'assistant';
  const fullText = msg.content || '';

  // Animasi hanya utk pesan assistant BARU yang ditandai (animate) & cukup panjang.
  const shouldAnimate = TYPEWRITER_ENABLED && animate && isAssistant && fullText.length >= TYPEWRITER_MIN_LEN;
  const len = fullText.length;
  const [revealLen, setRevealLen] = useState(shouldAnimate ? 0 : len);
  const doneRef  = useRef(!shouldAnimate);
  const rafRef   = useRef(0);
  const startRef = useRef(0);
  const onDoneRef = useRef(onAnimationDone);
  useEffect(() => { onDoneRef.current = onAnimationDone; });

  useEffect(() => {
    if (!shouldAnimate) { setRevealLen(len); doneRef.current = true; return; }
    doneRef.current = false;
    startRef.current = 0;
    const dur = Math.min(REVEAL_MAX_MS, Math.max(REVEAL_MIN_MS, (len / REVEAL_CPS) * 1000));
    const tick = (now) => {
      if (!startRef.current) startRef.current = now;
      const p = Math.min(1, (now - startRef.current) / dur);
      // ease-out lembut → aliran melambat mulus di akhir, tak berhenti mendadak.
      const eased = 1 - Math.pow(1 - p, 1.7);
      setRevealLen(Math.max(1, Math.round(len * eased)));
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setRevealLen(len);
        if (!doneRef.current) { doneRef.current = true; onDoneRef.current && onDoneRef.current(); }
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [fullText, shouldAnimate, len]);

  // Klik pesan → tampilkan penuh (skip animasi).
  const finishTyping = () => {
    cancelAnimationFrame(rafRef.current);
    setRevealLen(len);
    if (!doneRef.current) { doneRef.current = true; onDoneRef.current && onDoneRef.current(); }
  };
  const typing = shouldAnimate && revealLen < len;
  const shownText = msg.content;
  const handleCopy = () => {
    navigator.clipboard.writeText(msg.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (msg.role === 'user') {
    return (
      <div className="flex justify-end items-center gap-1.5 mb-3 group">
        {onStartEdit && (
          <button onClick={onStartEdit} title="Edit"
            className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-brand-600 flex-shrink-0">
            <Pencil style={{ width: 13, height: 13 }} />
          </button>
        )}
        <div className="max-w-[85%] bg-brand-600 text-white rounded-2xl rounded-tr-sm px-4 py-2.5">
          {/* [THUMB] Lampiran gambar pertanyaan ini. Bentuk datanya SAMA dengan
              yang dirender halaman riwayat (data_json.images), supaya transkrip
              hidup dan riwayat tidak pernah berbeda tampilan.

              Penjaganya `msg.images`, BUKAN `isCode`: berkas ini dipakai bersama
              oleh AICoding, AIWorkspace, AIPanel, dan AIChatHistory. Menjaga
              dengan isCode akan menyembunyikan lampiran justru saat sesi lama
              dibuka dari halaman lain.

              Miniatur yang gagal dibuat tetap muncul sebagai nama berkas —
              user perlu tahu ada gambar di sini walau tak bisa ditampilkan. */}
          {/* [TEXT ATTACH] Jejak berkas teks. Hanya nama dan ukuran — isinya
              sengaja tidak pernah disimpan (lihat toFileMarks). Tanpa jejak ini
              transkrip jadi menyesatkan: pertanyaan "kenapa error ini?" akan
              terbaca tanpa konteks apa pun, seolah user bertanya tanpa dasar,
              padahal ia melampirkan log. Penjaganya `msg.files`, BUKAN `isCode`
              — alasannya sama dengan blok gambar di bawah. */}
          {Array.isArray(msg.files) && msg.files.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {msg.files.map((fl, fi) => (
                <span key={fi} title={fl.name || ''}
                  className="text-[11px] bg-white/20 rounded-md px-2 py-1 max-w-[180px] truncate font-mono">
                  📄 {fl.name || 'file'}
                </span>
              ))}
            </div>
          )}
          {Array.isArray(msg.images) && msg.images.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {msg.images.map((im, ii) => (
                im.thumb ? (
                  <img key={ii} src={im.thumb} alt={im.name || 'image'}
                    title={im.name || 'image'}
                    onClick={() => onOpenImage?.(im)}
                    className="rounded-lg border border-white/30 object-cover cursor-zoom-in"
                    style={{ width: 72, height: 72 }} />
                ) : (
                  <span key={ii} title={im.name || ''}
                    className="text-[11px] bg-white/20 rounded-md px-2 py-1 max-w-[140px] truncate">
                    🖼 {im.name || 'image'}
                  </span>
                )
              ))}
            </div>
          )}
          <CollapsibleUserText text={msg.content} />
        </div>
      </div>
    );
  }
  if (msg.role === 'error') {
    return (
      <div className="flex mb-3">
        <div className="max-w-[90%] bg-red-50 border border-red-200 rounded-2xl rounded-tl-sm px-4 py-2.5">
          <p className="text-sm text-red-600">{msg.content}</p>
        </div>
      </div>
    );
  }
  if (msg.role === 'system-note') {
    // Output slash command (/help, /sql, /examples, dst) — gaya netral, bukan
    // jawaban AI. Render markdown agar list & code block (SQL) tampil rapi.
    return (
      <div className="flex mb-3">
        <div className="max-w-[90%] bg-slate-50 border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-2.5 text-slate-600">
          <MarkdownRenderer text={msg.content} />
        </div>
      </div>
    );
  }

  const isKb = msg.role === 'assistant' && Array.isArray(msg.sources) && msg.sources.length > 0;
  const score = (msg.role === 'assistant' && !hideAccuracy)
    ? (isKb ? calcKbScore(msg.content, msg.sources) : calcAccuracyScore(msg.content, hasRealData))
    : null;

  return (
    <div className="flex gap-2 mb-3 group">
      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Bot style={{ width: 14, height: 14 }} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
          <div
            onClick={typing ? finishTyping : undefined}
            title={typing ? t('ai.skipTyping') : undefined}
            className={typing ? 'cursor-pointer' : undefined}
          >
            <StreamingMarkdown
              text={shownText}
              revealLen={revealLen}
              animating={typing}
              sources={msg.sources}
            />
          </div>
          {/* [TOOLS] Bukti pemanggilan tool. Dirender SETELAH animasi ketik
              selesai (!typing), sama seperti tabel dan sumber di bawah:
              memunculkannya saat teks masih mengalir menggeser tata letak
              tepat ketika mata sedang membaca. */}
          {!typing && <ToolTrace tools={msg.tools} expected={!!msg.toolsExpected} />}
          {!typing && Array.isArray(msg.tableRows) && msg.tableRows.length > 0 && (
            <DataTable rows={msg.tableRows} animateIn={isLast} />
          )}
          {!typing && msg.sources && msg.sources.length > 0 && (
            <div className="mt-3 pt-2.5 border-t border-slate-100">
              <p className="text-xs font-semibold text-slate-400 mb-1.5 flex items-center gap-1">📄 {t('ai.sources')}</p>
              <div className="flex flex-wrap gap-1.5">
                {msg.sources.map((s, si) => (
                  <button key={si}
                    onClick={() => openNoteRef(s)}
                    title={`${t('ai.viewSource')} · ${t('ai.kbRelevance')} ${(s.score ?? 0).toFixed(2)}`}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-slate-50 hover:bg-brand-50 border border-slate-200 hover:border-brand-300 rounded-lg text-xs text-slate-600 hover:text-brand-700 transition-colors cursor-pointer">
                    <span className="font-medium">{s.title || t('ai.untitled')}</span>
                    <span className="text-[10px] text-slate-400 font-mono">{(s.score ?? 0).toFixed(2)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Baris meta selalu terlihat: sebelumnya opacity-0 group-hover:opacity-100,
            yang di perangkat sentuh berarti praktis tak pernah tampil (tidak ada
            hover) — badge "cached" pun jadi tak terbaca. */}
        {!typing && (
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          <button onClick={handleCopy} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600">
            {copied ? <Check style={{ width: 11, height: 11 }} /> : <Copy style={{ width: 11, height: 11 }} />}
            {copied ? t('ai.copied') : t('ai.copy')}
          </button>
          <SaveToMemoryButton content={msg.content} />
          <FeedbackButtons key={msg.id || 'pending'} msg={msg} />
          {msg.responseTime && <ResponseTimeBadge ms={msg.responseTime} />}
          {score && <AccuracyBadge score={score} />}
          {msg.totalTokens > 0 && (
            <TokenBadge
              promptTokens={msg.promptTokens || 0}
              completionTokens={msg.completionTokens || 0}
              totalTokens={msg.totalTokens || 0}
              estimated={msg.tokensEstimated}
            />
          )}
          {msg.dataSource && (
            <span className="text-xs text-slate-400 flex items-center gap-1" title={t('ai.dataSourceTitle')}>
              {msg.dataSource}
            </span>
          )}
          {msg.inheritedPeriods && msg.inheritedPeriods.length > 0 && (
            <span
              title={t('ai.inheritedPeriodTitle', { periods: msg.inheritedPeriods.join(' vs ') })}
              style={{ background: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe',
                borderRadius: 6, padding: '1px 6px', fontSize: 10, fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              {t('ai.inheritedPeriod')}
            </span>
          )}
          {msg.answerMode && MODE_CHIP[msg.answerMode] && (
            <span
              title={t(MODE_CHIP[msg.answerMode].titleKey)}
              style={{ background: MODE_CHIP[msg.answerMode].bg, color: MODE_CHIP[msg.answerMode].fg,
                border: `1px solid ${MODE_CHIP[msg.answerMode].bd}`,
                borderRadius: 6, padding: '1px 6px', fontSize: 10, fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              {t(MODE_CHIP[msg.answerMode].labelKey)}
            </span>
          )}
          {msg.cached && (
            <span
              title={lang === 'en'
                ? 'Answer served from semantic cache (table data re-run fresh).'
                : 'Jawaban dari semantic cache (data tabel tetap dieksekusi ulang).'}
              style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a',
                borderRadius: 6, padding: '1px 6px', fontSize: 10, fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <Database style={{ width: 9, height: 9 }} />
              {lang === 'en' ? 'cached' : 'cache'}
            </span>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

// ── Main AIChat ───────────────────────────────────────────────────────────────
// ── Penampil isi sumber KB ───────────────────────────────────────────────────
// Ada untuk VERIFIKASI: memperlihatkan teks chunk APA ADANYA — persis yang
// dibaca model saat menyusun jawaban.
//
// Sebelumnya klik sumber selalu menavigasi ke halaman Notes, yang benar hanya
// bila dokumennya memang note. Untuk dokumen hasil generate (source
// 'sap_customizing' dsb.) navigasi itu mendarat di note acak dan membuat sumber
// yang benar terlihat salah.
//
// Yang ditampilkan adalah CHUNK yang terambil, bukan gabungan seluruh dokumen.
// Itu disengaja: retrieval jarang memberi model dokumen utuh, dan menampilkan
// yang utuh akan membuat pembaca menyimpulkan model punya informasi yang
// sebetulnya tidak pernah ia terima.
function KbSourceModal({ src, onClose, t }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!src) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [src, onClose]);
  if (!src) return null;

  // `full` hanya ada pada respons setelah field ini ditambahkan; chat lama yang
  // dimuat dari riwayat tetap bisa dibuka, dengan cuplikan seadanya.
  const body = String(src.full || src.snippet || '');
  const truncated = !src.full && !!src.snippet;
  const navTarget = dashboardTargetOf(src);

  const doCopy = () => {
    try {
      navigator.clipboard.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* clipboard bisa ditolak browser */ }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4"
         onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-100 flex items-start gap-2">
          <FileText style={{ width: 16, height: 16 }} className="text-brand-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800 break-words">
              {src.title || t('ai.untitled')}
            </p>
            <p className="text-[11px] text-slate-400 font-mono mt-0.5 break-all">
              {src.chunk_id || src.doc_id}
              {src.kb_source ? ` · ${src.kb_source}` : ''}
              {` · ${t('ai.kbRelevance')} ${(src.score ?? 0).toFixed(2)}`}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div className="px-4 py-3 overflow-y-auto flex-1">
          <p className="text-[11px] text-slate-400 mb-2">{t('ai.kbChunkHint')}</p>
          <pre className="text-xs text-slate-700 whitespace-pre-wrap break-words font-mono leading-relaxed">
            {body}
          </pre>
          {truncated && (
            <p className="text-[11px] text-amber-600 mt-2">{t('ai.kbTruncated')}</p>
          )}
        </div>

        <div className="px-4 py-2.5 border-t border-slate-100 flex items-center gap-2">
          <button onClick={doCopy}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-lg">
            {copied ? <Check style={{ width: 12, height: 12 }} /> : <Copy style={{ width: 12, height: 12 }} />}
            {copied ? t('ai.copied') : t('ai.copy')}
          </button>
          {/* Tautan ke note asli DIPERTAHANKAN — untuk note sungguhan, jalur lama
              memang benar dan berguna. Yang dihapus hanya asumsi bahwa SEMUA
              sumber adalah note. */}
          {isNoteSource(src) && (
            <button onClick={() => { onClose(); gotoNote(src); }}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded-lg">
              <ExternalLink style={{ width: 12, height: 12 }} />
              {t('ai.kbOpenNote')}
            </button>
          )}
          {/* Lanjutkan verifikasi ke DATA aslinya. Muncul hanya bila sumbernya
              memang punya halaman asal yang bisa dituju — dokumen status
              transport bersifat landscape dan tidak menunjuk satu baris pun,
              jadi untuknya tombol ini sengaja tidak ada. */}
          {navTarget && (
            <button onClick={() => { onClose(); gotoDashboard(navTarget); }}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded-lg">
              <Table2 style={{ width: 12, height: 12 }} />
              {navTarget.label
                ? `${t('ai.kbOpenDashboard')} · ${navTarget.label}`
                : t('ai.kbOpenDashboard')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AIChat({ onClose, currentPath, activeKey, activeLabel, storageLabel, initialConfig, onConfigChange, maximized, onToggleMaximize, floating, onToggleFloat, pageContext, onAutoDetect, resolveDashboardLabel, suppressResetRef, isAutoPicked, resume, onSessionActivity }) {
  const [config, setConfig]             = useState(() => initialConfig || loadConfig() || getDefaultConfig());
  // Transkrip awal dari sesi yang dilanjutkan (lihat [RESUME] di bawah).
  // Lewat initializer, BUKAN useEffect: kalau lewat effect ada satu render
  // dengan transkrip kosong — quick prompts berkedip dan efek reset dashboard
  // bisa berjalan lebih dulu.
  const [messages, setMessages]         = useState(() =>
    (Array.isArray(resume?.messages) ? resume.messages : []));
  const [input, setInput]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [exportOpen, setExportOpen]     = useState(false);
  const exportRef = useRef(null);
  const [showPrompts, setShowPrompts]   = useState(() => !resume?.messages?.length);
  const [editingIdx, setEditingIdx]     = useState(-1);   // indeks pesan user yg sedang di-edit
  const [editText, setEditText]         = useState('');

  // Session ID — generate baru setiap kali chat di-clear / komponen mount baru.
  //
  // ── [RESUME] Melanjutkan sesi lama dari AI Chat History ────────────────────
  // Yang membuat ini bekerja BUKAN transkrip yang dipasang ke `messages`:
  // konteks percakapan hidup di AI Service, disimpan per session_id di
  // ai_chat_history miliknya sendiri, dan dibaca ulang setiap giliran (mode code
  // memakai AI_CODE_CONTEXT_TURNS giliran terakhir). Jadi cukup MEMAKAI ULANG
  // session_id lama dan model langsung ingat; transkrip di layar murni supaya
  // manusianya ikut melihat apa yang sedang dilanjutkan.
  //
  // Konsekuensinya: memuat seluruh transkrip TIDAK menambah token sama sekali,
  // karena isi yang dikirim ke model tidak berasal dari state ini.
  const sessionIdRef = useRef(resume?.session_id || genSessionId());

  // ── Pelaporan sesi ke induk (AIPanel) untuk sidebar riwayat ────────────────
  // Lewat ref, BUKAN dipanggil langsung dari dalam sendMessage: `onSessionActivity`
  // adalah arrow function baru di setiap render AIPanel, dan memasukkannya ke
  // dependency array sendMessage akan membuat callback itu dibuat ulang terus
  // sehingga memoisasinya kehilangan seluruh gunanya.
  const sessionActivityRef = useRef(onSessionActivity);
  useEffect(() => { sessionActivityRef.current = onSessionActivity; }, [onSessionActivity]);
  const reportSession = useCallback(() => {
    // Induk boleh tidak memasang prop ini (AICoding, AIWorkspace) — bukan error.
    try { sessionActivityRef.current?.(sessionIdRef.current); } catch (_) {}
  }, []);
  // Laporkan sesi awal sekali saat mount supaya sidebar bisa menyorot item yang
  // sedang dibuka setelah sesi lama dilanjutkan.
  useEffect(() => { reportSession(); }, [reportSession]);

  // Rotasi key info: { index, count } — update setiap kali pesan berhasil dikirim
  const [activeKeyInfo, setActiveKeyInfo] = useState({ index: 1, count: 1 });
  // rowCount dari server (filtered) — update setiap kali query SQLite berhasil
  const [serverRowCount, setServerRowCount] = useState(null);
  // Proactive switch toast
  const [proactiveToast, setProactiveToast] = useState(null);
  // [Text-to-SQL] Skema + mode dashboard ({ mode, schema, tables, maxRows })
  const [sqlSchema, setSqlSchema] = useState(null);

  const { dashboardData } = useDashboardData();
  const { t, lang } = useLang();
  const { hasAccess } = useAuth();
  const canConfigAI = hasAccess('ai_settings');
  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);

  useEffect(() => {
    if (initialConfig) setConfig(initialConfig);
  }, [initialConfig]);

  // Per-dashboard config (system_prompt + quick_prompts) — dashboardKey berasal
  // dari ROUTES (activeKey, diteruskan AIPanel). Setiap halaman sidebar punya key
  // stabil sendiri; fallback ke deteksi path hanya bila prop tidak tersedia.
  const dashboardKey = activeKey || getDashboardKey(currentPath);
  // Mode General (AI Workspace tanpa dashboard) → chat umum tanpa data SQL.
  const isGeneral = !dashboardKey;
  // Mode Coding (AI Workspace target "Coding") → pass-through ke LLM, tanpa
  // text-to-SQL / KB / auto-detect. Punya dashboardKey sendiri ('__code__')
  // supaya system prompt & quick prompts tetap dapat scope terpisah di
  // ai_dashboard_config — TAPI karena dashboardKey terisi, semua guard yang
  // memakai `!isGeneral` harus ikut mengecualikan mode ini.
  const isCode = dashboardKey === CODE_KEY;
  // [AGENT SDK] render ulang header saat dropdown Engine di halaman AI Coding diganti
  const [, setEngineTick] = useState(0);
  useEffect(() => {
    const h = () => setEngineTick((x) => x + 1);
    window.addEventListener('aiCoding.engine', h);
    return () => window.removeEventListener('aiCoding.engine', h);
  }, []);
  // [VISION] Lampiran gambar — HANYA mode Coding, karena hanya jalur Bedrock
  // (Nova Lite 2) yang bisa menerima gambar. Provider lain memaksa content
  // jadi string, jadi gambar akan hilang diam-diam.
  // Seluruh mekanismenya ada di useChatImages (state, validasi, kompresi,
  // paste, drag-drop). Hook SELALU dipanggil — `enabled` yang menentukan
  // aktif atau tidak — supaya aturan hooks React tidak dilanggar.
  const img = useChatImages({ enabled: isCode, t });
  // [TEXT ATTACH] Lampiran berkas teks — pasangan dari useChatImages, mode
  // Coding juga. Berbeda dari gambar, isinya menyatu ke `question` dan bukan
  // dikirim sebagai muatan tersendiri.
  //
  // Pesan galat ditulis DI SINI, bukan di dalam hook: penolakan ukuran harus
  // menunjuk ke panel Code sandbox, dan hook tidak punya urusan mengetahui
  // bahwa panel itu ada. Hook melaporkan JENIS kegagalan; halaman ini yang
  // memutuskan bagaimana menjelaskannya.
  const onFileError = useCallback((kind, info) => {
    const KB = Math.round(TEXT_MAX_BYTES / 1024);
    if (kind === 'size') {
      window.alert(
        `"${info.name}" berukuran ${Math.round(info.bytes / 1024)} KB, melewati batas ${KB} KB `
        + `untuk lampiran langsung.\n\n`
        + `Berkas sebesar ini dirancang untuk dibaca BERTAHAP, bukan ditempelkan ke pertanyaan. `
        + `Pakai panel "Code sandbox" di bar atas — berkasnya tersimpan di sana dan bisa dibaca `
        + `read_file sepotong demi sepotong dengan menyebut namanya di pertanyaan.`);
    } else if (kind === 'ext') {
      window.alert(`"${info.name}" bukan berkas teks yang didukung.\n\n`
        + `Yang diterima di sini: .log .csv .json .md .txt\n`
        + `Untuk PDF/Word/Excel, pakai panel "Code sandbox" — berkasnya dikonversi jadi .md di sana.`);
    } else if (kind === 'binary') {
      window.alert(`"${info.name}" tampaknya bukan teks biasa (ada byte yang tidak bisa dibaca `
        + `sebagai UTF-8). Isinya tidak akan berarti apa-apa bagi model, jadi tidak dilampirkan.`);
    } else if (kind === 'count') {
      window.alert(`Maksimal ${info.max} berkas teks per pertanyaan.`);
    } else {
      window.alert(`Gagal membaca "${info?.name || 'berkas'}".`);
    }
  }, []);
  const textFiles = useChatTextFiles({ enabled: isCode, onError: onFileError });
  // [THUMB] Klik miniatur di transkrip → penampil layar penuh. Komponen yang
  // sama dipakai halaman riwayat, jadi perilakunya identik di kedua tempat.
  const [lightbox, setLightbox] = useState(null);
  // Sumber KB yang sedang dibuka untuk verifikasi. Diisi lewat event karena
  // pemicunya ada di dua tempat: chip sumber di bawah jawaban, dan sitasi [n]
  // di dalam MarkdownRenderer — keduanya tidak perlu tahu di mana modal hidup.
  const [kbSrc, setKbSrc] = useState(null);
  useEffect(() => {
    const onOpen = (e) => setKbSrc(e.detail || null);
    window.addEventListener('ai:open-kb-doc', onOpen);
    return () => window.removeEventListener('ai:open-kb-doc', onOpen);
  }, []);

  // ── [PROGRESS] Fase permintaan yang sedang berjalan ────────────────────────
  // Satu permintaan bisa berjalan lebih dari semenit (loop tool memanggil model
  // berkali-kali). Spinner diam membuat "sedang memanggil tool" dan "koneksi
  // mati" terlihat persis sama; ini yang membedakannya.
  //
  // Polling, bukan streaming: proxy dashboard menyangga seluruh respons sebelum
  // meneruskan, jadi SSE menuntut penulisan ulang jalur respons dari ujung ke
  // ujung. Tampilan di sini tidak perlu berubah bila kelak diganti SSE.
  // [JOB] Job asinkron yang sedang dipantau: { id, startTime }. null = tidak ada.
  // Dideklarasikan DI SINI, bukan bersama sisa mesin job di bawah, karena efek
  // progress tepat di bawah ini membacanya — dan `const` yang dibaca sebelum
  // deklarasinya adalah ReferenceError saat render, bukan peringatan.
  const [job, setJob] = useState(null);
  const [phase, setPhase] = useState(null);
  useEffect(() => {
    if (!loading) { setPhase(null); return undefined; }
    // [JOB] Saat job aktif, fase datang dari /api/ai/job yang SEKALIGUS membawa
    // hasilnya. Membiarkan loop ini ikut jalan berarti dua permintaan tiap 1,5
    // detik untuk informasi yang sama — dan keduanya menulis `phase`, sehingga
    // nilai yang tampil bergantung pada mana yang kebetulan tiba belakangan.
    if (job) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get('/api/ai/progress', {
          params: { session_id: sessionIdRef.current },
        });
        if (alive) setPhase(data?.progress || null);
      } catch (_) {
        // Diam: indikator status yang gagal tidak boleh mengganggu pertanyaan
        // yang masih berjalan baik-baik saja.
      }
    };
    tick();
    const id = setInterval(tick, 1500);
    return () => { alive = false; clearInterval(id); };
  }, [loading, job]);

  const [dashCfg, setDashCfg] = useState({ system_prompt: '', quick_prompts: null });
  useEffect(() => {
    let alive = true;
    if (!dashboardKey) { setDashCfg({ system_prompt: '', quick_prompts: null }); return; }
    loadDashboardConfig(dashboardKey).then(cfg => { if (alive) setDashCfg(cfg); });
    return () => { alive = false; };
  }, [dashboardKey]);

  // [Text-to-SQL] Ambil mode + skema dashboard. Mode 'sql' → alur two-pass.
  useEffect(() => {
    let alive = true;
    // Mode coding tidak punya skema — jangan panggil endpoint sama sekali.
    if (!dashboardKey || dashboardKey === CODE_KEY) { setSqlSchema(null); return; }
    api.get(`/api/ai/sql-schema?dashboard=${encodeURIComponent(dashboardKey)}`)
      .then(r => { if (alive) setSqlSchema(r.data); })
      .catch(() => { if (alive) setSqlSchema(null); });
    return () => { alive = false; };
  }, [dashboardKey]);

  // Reset sesi chat saat PINDAH dashboard — cegah context & riwayat "bocor"
  // antar dashboard (mis. chat Solman terbawa saat membuka CloudTrail). Skip saat
  // mount pertama (prevDashKeyRef = nilai awal) agar tidak menghapus tanpa sebab.
  const prevDashKeyRef = useRef(dashboardKey);
  useEffect(() => {
    if (prevDashKeyRef.current === dashboardKey) return;
    prevDashKeyRef.current = dashboardKey;
    // Auto-detect (mode Umum) memanggil onAutoDetect → parent ganti activeKey
    // secara "diam-diam" untuk giliran BERIKUTNYA. suppressResetRef dipasang
    // true persis sebelum itu — riwayat chat TIDAK boleh terhapus dalam kasus
    // ini (beda dari user memilih dashboard lain secara manual di dropdown).
    if (suppressResetRef?.current) { suppressResetRef.current = false; return; }
    setMessages([]);
    setServerRowCount(null);
    setProactiveToast(null);
    setShowPrompts(true);
    sessionIdRef.current = genSessionId();
    reportSession();
  }, [dashboardKey]); // eslint-disable-line

  const provider     = AI_PROVIDERS[config.provider] || AI_PROVIDERS.groq;
  // [CODE MODEL] Badge header harus menampilkan provider/model yang BENAR-BENAR
  // dipakai. Di mode Coding itu bisa berbeda dari config global. Logika di sini
  // mencerminkan resolusi di AI Service — termasuk guard "provider beda ⇒ model
  // global tidak diwariskan". Satu hal yang TIDAK terlihat dari browser:
  // fallback AI_CODE_PROVIDER/AI_CODE_MODEL dari env. Bila keduanya diisi di
  // ecosystem.config.js sementara kolom ai_configs kosong, badge akan menunjuk
  // config global padahal env yang menang — log [ai/code] tetap sumber pasti.
  const codeProvKey  = isCode ? String(config.codeProvider || '').trim() : '';
  const codeModelStr = isCode ? String(config.codeModel    || '').trim() : '';
  // [AGENT SDK] Bila Engine = Agent SDK, yang menjawab adalah agen di host DR, bukan provider Coding Mode.
  const _agentEng = isCode ? (() => { try { return localStorage.getItem('aiCoding.engine') || ''; } catch (_) { return ''; } })() : '';
  const badgeProvider = _agentEng ? { logo: '🤖', name: 'Claude Agent SDK' } : (codeProvKey ? (AI_PROVIDERS[codeProvKey] || provider) : provider);
  const badgeModel    = _agentEng ? (_agentEng === 'default' ? 'default profile' : _agentEng + ' profile') : codeModelStr
    || (codeProvKey && codeProvKey !== config.provider
          ? (AI_PROVIDERS[codeProvKey]?.defaultModel || '')
          : config.model);
  const badgeOverridden = isCode && !!(codeProvKey || codeModelStr);
  const dashCtx      = getDashboardContext(dashboardKey, activeLabel);
  // Nama yang DISIMPAN ke kolom `dashboard` di ai_chat_history. Sengaja dipisah
  // dari dashCtx.name (yang dipakai untuk TAMPILAN: badge header, judul PDF,
  // AISettings) karena keduanya punya syarat berlawanan: tampilan harus ikut
  // bahasa UI, penyimpanan harus stabil lintas bahasa agar bisa difilter.
  // Pemanggil yang labelnya sudah statis tidak perlu mengirim storageLabel —
  // fallback ke activeLabel membuat perilaku lama tidak berubah sama sekali.
  const storeName    = getDashboardContext(dashboardKey, storageLabel || activeLabel).name;
  const dataContext  = buildDataContext(currentPath, dashboardData);
  const hasRealData  = !!dataContext || (serverRowCount ?? 0) > 0;
  // Quick prompts kustom (AISettings) disimpan sebagai objek bersarang:
  //   { [lang]: { "<dashboardKey>.data": [...], "<dashboardKey>": [...] } }
  // Versi lama bisa berupa array datar — keduanya didukung. Kalau tak ada kustom,
  // pakai preset berbasis dashboardKey.
  const customQuick = (() => {
    // dashboard → dashCfg.quick_prompts; general (tanpa dashboard) → config.quickPrompts global
    const qp = dashboardKey ? dashCfg?.quick_prompts : config.quickPrompts;
    const dkey = dashboardKey || '__global__';
    if (!qp) return null;
    if (Array.isArray(qp)) {                      // legacy: array datar
      const a = qp.filter(s => s && s.trim());
      return a.length ? a : null;
    }
    if (typeof qp === 'object') {
      const byLang = qp[lang] || qp.id || qp.en || {};
      const arr = byLang[`${dkey}.data`] || byLang[dkey];
      if (Array.isArray(arr)) {
        // sentinel ['__none__'] = sengaja dikosongkan → tampilkan NOL prompt
        if (arr.length === 1 && arr[0] === '__none__') return [];
        const a = arr.filter(s => s && s.trim());
        return a.length ? a : null;
      }
      return null;
    }
    return null;
  })();
  const quickPrompts = customQuick !== null ? customQuick : getQuickPrompts(dashboardKey, hasRealData, lang);
  const isConfigured = !provider.requiresKey || config.hasKey || (config.apiKey && config.apiKey.trim().length > 0);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);
  useEffect(() => { if (!showSettings) inputRef.current?.focus(); }, [showSettings]);

  const handleSlashCommand = useSlashCommands({
    t, messages, quickPrompts, sessionIdRef, genSessionId,
    setMessages, setInput, setShowPrompts, setActiveKeyInfo,
    setProactiveToast, setServerRowCount, onAutoDetect,
  });

  // ── [JOB] Menerapkan jawaban ke UI + riwayat ───────────────────────────────
  // Dipisah dari sendMessage karena sekarang ada DUA pemanggil: jalur sinkron
  // (respons langsung dari /api/ai/ask) dan jalur job (respons yang dipungut
  // lewat polling, mungkin beberapa menit kemudian, mungkin setelah browser
  // di-reload). Isinya TIDAK diubah dari versi sebelumnya — kalau kedua jalur
  // kelak menampilkan hasil yang berbeda, penyebabnya bukan di sini.
  //
  // `startTime` diteruskan pemanggil, bukan dihitung di sini: pada jalur job,
  // waktu yang bermakna adalah sejak user menekan kirim, bukan sejak hasilnya
  // dipungut.
  const applyAnswer = useCallback((r, { modePayload, startTime, userText }) => {
    const reply        = r.answer || '';
    const responseTime = Date.now() - startTime;
    const aiDataSource = dataSourceLabel(r, {
      rag: t('ai.srcRag'), sql: t('ai.srcSql'), chat: t('ai.srcChat'),
    });
    if (r.sql) setServerRowCount((r.rows || []).length);

    // `userText` WAJIB diteruskan pemanggil, bukan diambil dari closure.
    // Saat blok ini masih berada di dalam sendMessage ia memang ada di lingkup;
    // setelah dipisah, referensi yang tertinggal menjadi ReferenceError yang
    // hanya meledak pada JALUR JOB — dan karena lemparannya menghentikan tick
    // sebelum sempat mematikan loading, gejalanya bukan pesan galat melainkan
    // spinner yang menggantung selamanya padahal jawaban sudah tersimpan utuh.
    const tok        = normalizeUsage(r.usage, userText || '', reply);
    const answerMode = answerModeOf(r, modePayload);

    const assistantMsg = {
      role: 'assistant',
      content: reply,
      answerMode,
      inheritedPeriods: Array.isArray(r.inherited_periods) ? r.inherited_periods : null,
      responseTime,
      hasData: !!(r.rows && r.rows.length),
      dataSource: aiDataSource,
      aiServiceSql: r.sql || null,         // SQL yang dipakai (untuk transparansi)
      promptTokens:     tok.prompt,
      completionTokens: tok.completion,
      totalTokens:      tok.total,
      tokensEstimated:  tok.estimated,
      sources:          r.sources || [],
      cached:           !!r.cached,           // badge "cache" bila dari semantic cache AI Service
      tableRows:        Array.isArray(r.rows) ? r.rows : null,   // data asli utk tabel
      // [TOOLS] Jejak tool yang BENAR-BENAR dipanggil AI Service pada giliran
      // ini. Sudah lama dikirim di respons (`tools_used`) dan diteruskan utuh
      // oleh proxy dashboard, tapi tidak pernah dibaca — sehingga satu-satunya
      // dasar untuk mempercayai "saya sudah cek knowledge base" adalah
      // klaim model itu sendiri. Sekarang klaimnya bisa diperiksa.
      tools:            Array.isArray(r.tools_used) && r.tools_used.length ? r.tools_used : null,
      // Hanya mode Coding yang punya tool. Ditandai per-pesan, bukan dibaca
      // dari isCode saat render: satu sesi bisa dibuka kembali dari halaman
      // lain, dan peringatan "tanpa memeriksa kode" akan salah tempat kalau
      // bergantung pada mode yang sedang aktif alih-alih mode saat dijawab.
      toolsExpected:    !!isCode,
      // Model yang BENAR-BENAR menjawab giliran ini. Sumbernya sama dengan
      // yang disimpan ke DB beberapa baris di bawah — dilekatkan ke pesan
      // supaya ekspor bisa membacanya. Tanpa ini ekspor memakai config global,
      // dan mode Coding (yang meng-override provider/model) akan dilaporkan
      // dengan nama model yang tidak pernah menjawab.
      answeredBy:       (r.provider_used || r.model_used)
        ? { provider: r.provider_used || '', model: r.model_used || '' } : null,
      _animate:         true,   // efek ketik (Opsi C) — hanya pesan baru
      // [REVIEW] Kunci lokal untuk menempelkan id DB setelah tersimpan.
      _localKey:        'a_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
    };
    setMessages(prev => [...prev, assistantMsg]);

    // Tetap simpan ke history dashboard agar AIChatHistory terus berfungsi.
    // PENTING: sertakan token — tanpa ini kolom token DB selalu 0 (bug lama).
    // data: rows + sources → agar History bisa render tabel/chart spt live.
    saveChatMessage({
      session_id: sessionIdRef.current,
      dashboard:  storeName,
      // Provider/model yang BENAR-BENAR dipakai, dilaporkan AI Service.
      // JANGAN pakai config.* — mode Coding meng-override ke code_provider/
      // code_model, dan model kosong dijatuhkan ke DEFAULT_MODELS di server.
      // Fallback ke config hanya untuk AI Service versi lama yang belum
      // mengirim field ini.
      provider:   r.provider_used || config.provider,
      model:      r.model_used    || config.model,
      message_role: 'assistant',
      content:    reply,
      tokens_prompt:     tok.prompt,
      tokens_completion: tok.completion,
      tokens_total:      tok.total,
      tokens_estimated:  tok.estimated ? 1 : 0,
      response_time: responseTime,
      has_data:   !!(r.rows && r.rows.length),
      data_source: aiDataSource + (r.cached ? ' semantic-cache' : ''),
      cached: !!r.cached,   // kolom eksplisit di ai_chat_history → filter WHERE cached=1
      data: {
        rows:    Array.isArray(r.rows)    ? r.rows.slice(0, 200) : undefined,
        sources: Array.isArray(r.sources) ? r.sources            : undefined,
        // Server meringkas argumennya (slimTools) — input write_file berisi
        // seluruh isi berkas dan tidak boleh mendarat utuh di DB.
        tools:   Array.isArray(r.tools_used) && r.tools_used.length ? r.tools_used : undefined,
        tools_expected: isCode ? 1 : undefined,
        // [REVIEW] SQL Text-to-SQL disimpan untuk bahan review jawaban.
        sql:     r.sql || undefined,
      },
    }).then(id => {
      if (!id) return;
      setMessages(prev => prev.map(m => m._localKey === assistantMsg._localKey ? { ...m, id } : m));
    });
  }, [storeName, config, isCode, t]);


  // ── [JOB] Jejak pekerjaan asinkron yang bertahan melewati reload ───────────
  // Disimpan di localStorage, bukan state React maupun sessionStorage:
  //   - state React hilang saat komponen unmount (pindah halaman);
  //   - sessionStorage hilang saat TAB ditutup, dan "tutup tab lalu buka lagi
  //     nanti" justru salah satu skenario yang fitur ini ada untuk melayani.
  // Kunci per-sesi supaya dua sesi Coding berbeda tidak saling menimpa jejak.
  const rememberJob = useCallback((sid, rec) => {
    try { window.localStorage.setItem(`ai_job:${sid}`, JSON.stringify(rec)); } catch (_) {}
  }, []);
  const forgetJob = useCallback((sid) => {
    try { window.localStorage.removeItem(`ai_job:${sid}`); } catch (_) {}
  }, []);

  // ── [JOB] Menyambung kembali setelah reload / buka ulang sesi ─────────────
  // Berjalan sekali saat sesi Coding dibuka. Kalau ada jejak job untuk sesi
  // ini, pemantauan dilanjutkan seolah user tidak pernah pergi.
  //
  // Dibaca dari localStorage, BUKAN dengan bertanya ke server lewat
  // ?session_id=: jejak lokal memberi tahu `startTime` yang asli (kapan user
  // menekan kirim), dan tanpa itu durasi yang ditampilkan akan dihitung dari
  // saat halaman dibuka — angka yang terlihat masuk akal tapi salah.
  useEffect(() => {
    if (!isCode) return;
    const sid = sessionIdRef.current;
    if (!sid) return;
    let rec = null;
    try {
      const raw = window.localStorage.getItem(`ai_job:${sid}`);
      rec = raw ? JSON.parse(raw) : null;
    } catch (_) { rec = null; }
    if (!rec || !rec.jobId) return;
    setJob({ id: rec.jobId, startTime: rec.startTime || Date.now(), userText: rec.userText || '' });
    setLoading(true);
  }, [isCode]);

  // ── [JOB] Polling hasil ───────────────────────────────────────────────────
  // Menggantikan polling /api/ai/progress selama ada job aktif: satu endpoint
  // sudah membawa fase DAN hasil sekaligus, jadi dua loop paralel hanya
  // menggandakan permintaan tanpa menambah informasi.
  useEffect(() => {
    if (!job || !job.id) return undefined;
    let alive = true;
    const jobId = job.id;
    const startTime = job.startTime;
    const askedText = job.userText || '';

    const tick = async () => {
      let data = null;
      try {
        const resp = await api.get('/api/ai/job', { params: { id: jobId } });
        data = resp.data;
      } catch (e) {
        // ── Galat SEMENTARA vs galat PERMANEN ────────────────────────────────
        // Kalimat lama di sini — "gagal BERTANYA bukan gagal BEKERJA" — tetap
        // benar untuk jaringan yang putus sebentar, dan itu memang inti fitur
        // ini. Tapi ia menyamakan dua hal yang berbeda sifatnya: jaringan pulih
        // sendiri, sesi login TIDAK. Ketika token kedaluwarsa (masa berlaku 8
        // jam, `server.js:344`) setiap tick berikutnya keluar di titik ini,
        // SEBELUM `setPhase`. Loop-nya sendiri terus berjalan — yang membeku
        // adalah indikator fasenya, dan spinner tidak pernah mati. Job selesai
        // dengan benar di server, jawabannya utuh di `ai_jobs`, tapi tidak
        // pernah sampai ke layar. Terjadi 22 Agu 2026 ~19:57.
        //
        // Catatan status, karena mudah salah: token KEDALUWARSA memulangkan
        // **403** (`server.js:217`), bukan 401. Yang 401 adalah token hilang
        // (215) dan `SESSION_REVOKED` (225). Ketiganya sama-sama permanen dari
        // sudut pandang loop ini, jadi ketiganya ditangani sama.
        const st = e?.response?.status;
        if (st === 401 || st === 403) {
          if (!alive) return;
          stopRef.current = true;   // cegah `loop` menjadwalkan tick berikutnya
          // Jejak job SENGAJA TIDAK dihapus dari localStorage. Efek yang
          // diandalkan: setelah login ulang, useEffect pemulihan di atas
          // membaca `ai_job:<sid>` dan polling menyambung sendiri — jawaban
          // yang sudah jadi tetap terambil, bukan hilang.
          setMessages(prev => [...prev, {
            role: 'system-note',
            content: '🔐 Sesi login berakhir saat permintaan masih dikerjakan. '
                   + 'Pekerjaannya TETAP berjalan di server dan jawabannya tersimpan. '
                   + 'Silakan login ulang lalu buka kembali chat ini — jawabannya akan muncul sendiri.',
          }]);
          setLoading(false);
          return;
        }
        // Sisanya diperlakukan seperti dulu: diamkan, coba lagi tick berikutnya.
        return;
      }
      if (!alive) return;
      const j = data && data.job;
      const sid = sessionIdRef.current;

      // Job tidak ditemukan (mis. sudah dipangkas setelah 24 jam). Tidak ada
      // lagi yang bisa ditunggu; lepaskan jejaknya supaya sesi ini tidak
      // mencoba menyambung selamanya setiap kali dibuka.
      if (!j) { forgetJob(sid); setJob(null); setLoading(false); return; }

      setPhase({
        phase: j.phase, detail: j.detail, step: j.step, total: j.total,
        age_ms: 0, elapsed_ms: j.elapsed_ms || 0,
      });
      if (!j.done) return;

      // Pembungkus try/catch di sini BUKAN kehati-hatian berlebihan. Tanpa ini,
      // satu lemparan di dalam applyAnswer menghentikan tick SEBELUM baris-baris
      // penutup di bawah — forgetJob, setJob(null), setLoading(false) — sempat
      // berjalan. Gejalanya bukan pesan galat melainkan spinner yang menggantung
      // selamanya, padahal job sudah selesai dan jawabannya utuh di server:
      // kegagalan yang tampak persis seperti masalah yang fitur ini justru
      // dibuat untuk menghapus. Itu terjadi pada 22 Agu 2026 karena satu
      // referensi `userText` yang tertinggal di luar lingkup.
      if (j.status === 'done' && j.result) {
        try {
          applyAnswer(j.result, { modePayload: null, startTime, userText: askedText });
        } catch (e) {
          const msg = `❌ Jawaban diterima tetapi gagal ditampilkan: ${e.message}`;
          setMessages(prev => [...prev, { role: 'error', content: msg }]);
          saveChatMessage({ session_id: sid, dashboard: storeName, message_role: 'error', content: msg });
        }
      } else {
        // 'interrupted' dibedakan dari galat biasa, dan kalimatnya sengaja
        // menyebut apa yang terjadi pada PEKERJAAN, bukan pada koneksi.
        // "Network Error" dulu muncul untuk keadaan seperti ini dan
        // menyesatkan ke arah jaringan user.
        const msg = j.status === 'interrupted'
          ? '⚠️ Permintaan terputus karena AI Service berhenti saat sedang mengerjakannya. Silakan kirim ulang.'
          : `❌ ${j.error_text || (j.result && j.result.error) || 'Permintaan gagal.'}`;
        setMessages(prev => [...prev, { role: 'error', content: msg }]);
        saveChatMessage({ session_id: sid, dashboard: storeName, message_role: 'error', content: msg });
      }
      forgetJob(sid);
      setJob(null);
      setLoading(false);
      reportSession();
    };

    // ── Jeda ADAPTIF, bukan interval tetap ───────────────────────────────────
    // Interval 1,5 detik yang seragam terasa sangat berbeda tergantung panjang
    // pekerjaan: pada job 33 detik tambahannya 4% dan tak terasa, tapi pada job
    // 2,8 detik ia bisa berarti menunggu 50% lebih lama dari yang seharusnya —
    // dan justru pertanyaan pendek yang paling sering dikirim. Jalur sinkron
    // yang lama tidak punya jeda sama sekali, jadi tanpa penyesuaian ini
    // perpindahan ke job terasa sebagai KEMUNDURAN untuk kasus tercepat.
    //
    // Enam detik pertama dipepatkan ke 400 ms (menangkap hampir semua job
    // pendek nyaris seketika), sesudahnya melebar ke 1,5 detik supaya job
    // panjang tidak menghasilkan ratusan permintaan sia-sia.
    //
    // setTimeout berantai, BUKAN setInterval: dengan interval tetap, tick yang
    // lambat bisa menumpuk di belakang tick berikutnya. Berantai menjamin
    // permintaan berikutnya baru dijadwalkan setelah yang sekarang selesai.
    let timer = null;
    // Bendera berhenti yang TERPISAH dari `alive`. `alive` dimiliki cleanup
    // effect (unmount / job berganti); memakainya untuk berhenti karena sesi
    // habis akan mencampur dua sebab yang berbeda dan menyulitkan penelusuran.
    const stopRef = { current: false };
    const loop = async () => {
      await tick();
      if (!alive || stopRef.current) return;
      const delay = (Date.now() - startTime) < 6000 ? 400 : 1500;
      timer = setTimeout(loop, delay);
    };
    loop();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [job, applyAnswer, forgetJob, reportSession, storeName]);

  // Quick question: tulis ke kotak input (bisa diedit dulu), bukan langsung kirim.
  const fillInput = (q) => {
    setInput(q);
    setTimeout(() => {
      const el = inputRef.current;
      if (el) { el.focus(); try { el.setSelectionRange(q.length, q.length); } catch (e) {} }
    }, 0);
  };

  // [QUICK-EDIT] Kelola quick question langsung dari panel chat — disimpan per
  // user + dashboard (ai_dashboard_config) di bahasa aktif, kunci `<dash>.data`.
  // list=null → hapus kustom (kembali ke preset); [] → sentinel '__none__'.
  const [qAdding, setQAdding] = useState(false);
  const [qDraft, setQDraft]   = useState('');
  const [qBusy, setQBusy]     = useState(false);
  const canEditQuick = !!dashboardKey;
  const saveQuick = async (list) => {
    if (!dashboardKey) return false;
    const cur = dashCfg?.quick_prompts;
    const base = (cur && typeof cur === 'object' && !Array.isArray(cur)) ? JSON.parse(JSON.stringify(cur)) : {};
    const L = lang || 'id';
    base[L] = { ...(base[L] || {}) };
    delete base[L][dashboardKey];
    if (list === null) delete base[L][`${dashboardKey}.data`];
    else base[L][`${dashboardKey}.data`] = list.length ? list : ['__none__'];
    setQBusy(true);
    try {
      await saveDashboardConfig(dashboardKey, { system_prompt: dashCfg?.system_prompt || '', quick_prompts: base });
      setDashCfg(prev => ({ ...(prev || {}), quick_prompts: base }));
      return true;
    } catch (e) {
      console.warn('[AIChat] save quick prompts failed:', e.message);
      return false;
    } finally { setQBusy(false); }
  };
  const addQuick = async (text) => {
    const q = String(text || '').trim();
    if (!q) return;
    if (!quickPrompts.includes(q) && !(await saveQuick([...quickPrompts, q]))) return;
    setQAdding(false); setQDraft('');
  };
  const removeQuick = (q) => saveQuick(quickPrompts.filter(x => x !== q));
  const renderQuickAdder = (compact) => (
    <div className={`flex items-center gap-1.5 ${compact ? 'mb-2' : 'mt-1.5'}`}>
      <input autoFocus value={qDraft} maxLength={300}
        onChange={e => setQDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); addQuick(qDraft); }
          if (e.key === 'Escape') { e.preventDefault(); setQAdding(false); setQDraft(''); }
        }}
        placeholder="Type a quick question, then Enter"
        className="flex-1 min-w-0 px-3 py-1.5 rounded-lg border border-brand-300 text-xs outline-none focus:ring-2 focus:ring-brand-100" />
      <button type="button" onClick={() => addQuick(qDraft)} disabled={qBusy || !qDraft.trim()}
        className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-brand-600 text-white disabled:opacity-40">
        {qBusy ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" /> : 'Add'}
      </button>
      <button type="button" onClick={() => { setQAdding(false); setQDraft(''); }}
        className="p-1 text-slate-400 hover:text-slate-600" title="Cancel">
        <X style={{ width: 12, height: 12 }} />
      </button>
    </div>
  );

  const sendMessage = useCallback(async (text) => {
    let userText = (text || input).trim();
    // Gambar tanpa teks: backend menolak `question` kosong (400), jadi isi
    // pertanyaan default alih-alih membiarkan kirim gagal senyap.
    if (!userText && img.attachedImgs.length) {
      userText = img.attachedImgs.length > 1
        ? 'Jelaskan runtutan proses pada gambar-gambar berikut, urut sesuai nomornya.'
        : 'Jelaskan isi gambar ini.';
    }
    // Sama alasannya dengan cabang gambar di atas: backend menolak `question`
    // kosong dengan 400. Berkas terlampir tanpa pertanyaan hampir selalu
    // berarti "lihat ini", jadi lebih baik diberi pertanyaan bawaan daripada
    // kirim gagal senyap.
    if (!userText && textFiles.files.length) {
      userText = textFiles.files.length > 1
        ? 'Analisa isi berkas-berkas terlampir berikut.'
        : `Analisa isi berkas ${textFiles.files[0].name} berikut.`;
    }
    if (!userText || loading) return;
    if (!isConfigured) { if (canConfigAI) setShowSettings(true); return; }

    // Slash command diproses lokal; bila tertangani, berhenti di sini.
    if (handleSlashCommand(userText)) return;

    setInput('');
    setShowPrompts(false);
    setLoading(true);
    // [THUMB] Jejak lampiran diambil DI SINI, sebelum request dikirim — bukan
    // setelahnya. Alur kirim memanggil img.clearImages() begitu request
    // berhasil, jadi mengambilnya belakangan selalu menghasilkan array kosong.
    // Itulah sebabnya toImageMarks() sudah lama ada tapi thumbnail tidak
    // pernah muncul di transkrip: fungsinya tidak pernah dipanggil sama sekali.
    const imageMarks = img.toImageMarks();
    // [TEXT ATTACH] Jejak berkas: NAMA dan UKURAN saja, isinya tidak ikut.
    // `content` di transkrip tetap persis apa yang user ketik — isi berkas
    // hanya menempel pada muatan yang DIKIRIM (lihat sentText di bawah).
    // Memisahkannya begini membuat riwayat tetap terbaca: log 50 KB yang ikut
    // tersimpan akan mengubur pertanyaannya sendiri, dan ikut membengkakkan
    // setiap backup karena `VACUUM INTO` menyalin seluruh database.
    const fileMarks = textFiles.toFileMarks();
    const userMsg = {
      role: 'user', content: userText,
      ...(imageMarks.length ? { images: imageMarks } : {}),
      ...(fileMarks.length ? { files: fileMarks } : {}),
    };
    setMessages(prev => [...prev, userMsg]);

    // Persist user message.
    // provider/model SENGAJA dikosongkan: pesan user bukan panggilan model,
    // jadi tidak ada yang bisa diklaim. Ini juga memperbaiki tampilan riwayat —
    // daftar sesi memakai MAX(provider) lintas baris, dan string kosong selalu
    // kalah terhadap nilai sungguhan dari baris assistant. Dulu baris ini
    // menulis config.provider (setelan GLOBAL) sehingga sesi AI Coding tampil
    // sebagai 'groq' walau sebenarnya dijawab Bedrock.
    saveChatMessage({
      session_id: sessionIdRef.current,
      dashboard:  storeName,
      message_role: 'user',
      content:    userText,
      // Hanya MINIATUR yang mendarat di DB (server memotong lagi lewat
      // slimImages). Gambar penuh tetap sekali pakai: DB ini ikut tersalin utuh
      // setiap `VACUUM INTO`, jadi menyimpan aslinya akan menggelembungkan
      // seluruh rantai backup.
      // [TEXT ATTACH] `files` menumpang objek `data` yang SAMA. Menulis dua
      // spread `data:` berturut-turut akan membuat yang kedua MENIMPA yang
      // pertama — pesan dengan gambar DAN berkas akan kehilangan gambarnya,
      // senyap. Karena itu keduanya dirakit sekali.
      ...((imageMarks.length || fileMarks.length) ? {
        data: {
          ...(imageMarks.length ? { images: imageMarks } : {}),
          ...(fileMarks.length ? { files: fileMarks } : {}),
        },
      } : {}),
    });
    // Sesi baru baru benar-benar ADA di ai_chat_history setelah baris ini.
    // Melapor lebih awal (mis. saat mount) membuat sidebar mencari sesi yang
    // belum punya satu baris pun.
    reportSession();

    const startTime = Date.now();
    // `return` di dalam try TETAP menjalankan `finally`. Tanpa penanda ini,
    // jalur job akan mematikan loading sepersekian detik setelah kirim —
    // spinner hilang, polling jalan diam-diam, dan user mengira kirimannya
    // gagal. Penanda dipasang HANYA saat pekerjaan benar-benar diserahkan.
    let handedOff = false;
    try {
      // ── [AI Service] Satu panggilan; AI Service yang mengorkestrasi
      // SQL + jawaban. Dulu ini salah satu cabang dari sebuah saklar;
      // jalur lama (pass-1 → run-sql → pass-2 → chart) sudah dibuang karena
      // tidak pernah lagi tereksekusi sejak saklar dipaku ke true.
      // Halaman dashboard yang tak terdaftar sebagai source SQL → tak bisa
      // dilayani. Dikecualikan: (a) mode General (isGeneral) → chat umum;
      // (b) halaman LIVE yang menyediakan pageContext (Opsi B) → jalur context.
      const hasPageContext = hasPageContextOf(pageContext);
      if (isUnservablePage({ isGeneral, isCode, sqlSchema, hasPageContext })) {
        const note = t('aiChat.aiAssistantNotAvailablePage');
        setMessages(prev => [...prev, { role: 'error', content: note }]);
        return; // finally tetap setLoading(false)
      }

      // ── [Auto-detect] Tanpa SQL source aktif (mode Umum / halaman tanpa
      // page-context): deteksi dashboard relevan dari isi pertanyaan via
      // backend /api/ai/route-question (LLM cepat + fallback keyword), lalu
      // pakai sbg `source` utk /ai/ask — AI Service jalankan text-to-SQL-nya.
      let effSource = dashboardKey || '__general__';
      // "Tanpa SQL aktif" ATAU dashboard aktif saat ini adalah HASIL auto-detect
      // sebelumnya (bukan pilihan manual user) — supaya topik berikutnya yang
      // beda (mis. "AWS bill" lalu "status M365") tetap dideteksi ulang, tidak
      // terkunci ke dashboard hasil deteksi giliran sebelumnya.
      if (needsAutoDetect({ isCode, sqlSchema, hasPageContext, isAutoPicked })) {
        try {
          const { data: rq } = await api.post('/api/ai/route-question', { question: userText });
          // DIAGNOSTIK SEMENTARA — hapus setelah fitur terkonfirmasi bekerja.
          setMessages(prev => [...prev, { role: 'system-note', content: `🐛 router → dashboard=${JSON.stringify(rq?.dashboard ?? null)} method=${rq?.method || '?'}${rq?.reason ? ` reason=${rq.reason}` : ''}` }]);
          if (rq?.dashboard) {
            const label = resolveDashboardLabel ? resolveDashboardLabel(rq.dashboard) : rq.dashboard;
            effSource = rq.dashboard;
            setMessages(prev => [...prev, { role: 'system-note', content: `🔀 ${t('ai.autoDetected')}: **${label}**` }]);
            onAutoDetect?.(rq.dashboard);
          }
          // rq.dashboard null → TIDAK ada topik baru terdeteksi. Ini BUKAN
          // sinyal "kembali ke General" — follow-up wajar (mis. "breakdown
          // per bulan" setelah bicara AWS Cost) memang tidak mengandung kata
          // kunci apa pun. Biarkan effSource tetap default (dashboard yang
          // sedang aktif), supaya konteks percakapan tidak putus.
        } catch (e) {
          setMessages(prev => [...prev, { role: 'system-note', content: `🐛 route-question GAGAL: ${e.response?.status || ''} ${e.response?.data?.error || e.message}` }]);
        }
      }
      const resolvedModel = resolveModelId(config);
      const modePayload = buildModePayload({
        isCode, hasPageContext, sqlSchema, pageContext, activeLabel,
        followUpOn: config.followUp !== false,
        userText, messages,
      });
      // [VISION] Konversi lampiran → { format, b64 }. Hanya untuk mode
      // Coding; di mode lain lampiran memang tidak bisa dipasang.
      const sentImgParts = img.toImageParts();
      // [TEXT ATTACH] Isi berkas ditempelkan HANYA di sini — ke muatan yang
      // dikirim, bukan ke `userText` yang dipakai di atas.
      //
      // Urutannya penting dan sengaja: buildModePayload sudah dipanggil dengan
      // `userText` ASLI. Kalau isi berkas ikut ke sana, deteksi follow-up
      // (isFollowUpQuery) akan menilai ribuan baris log alih-alih pertanyaan
      // pendek user, dan hampir pasti salah menyimpulkan.
      const sentBlocks = textFiles.toPromptBlocks();
      const sentText   = sentBlocks ? `${userText}${sentBlocks}` : userText;
      // [AGENT SDK] Engine selector on the AI Coding page ('aiCoding.engine')
      let agentText = sentText;
      if (isCode && !/^\/agent(\s|$)/i.test(userText)) {
        let eng = ''; try { eng = localStorage.getItem('aiCoding.engine') || ''; } catch (_) {}
        if (eng) agentText = `/agent ${eng === 'default' ? '' : '@' + eng + ' '}${sentText}`;
      }
      const askBody = buildAskBody({
        effSource, userText: agentText,
        sessionId: sessionIdRef.current,
        config, resolvedModel, lang,
        modePayload,
        imageParts: sentImgParts,
      });

      // ── [JOB] Mode Coding → jalur ASINKRON ───────────────────────────────
      // Pekerjaan dilepas dari koneksi: kirim, terima job_id, lalu polling.
      // Socket yang putus di tengah tidak lagi membuang jawaban, karena
      // jawabannya memang tidak pernah menempel di socket.
      //
      // Mode lain TETAP sinkron. Jalur SQL/KB selesai di bawah 10 detik;
      // memberinya mesin job hanya menambah permukaan risiko.
      if (isCode) {
        let jobId = null;
        try {
          const { data: sub } = await api.post('/api/ai/ask', { ...askBody, async_job: true });
          jobId = sub?.job_id || null;
        } catch (e) {
          // 409 = sesi ini masih punya pekerjaan berjalan. BUKAN kegagalan:
          // menyambung ke job itu persis yang user maksud saat menekan kirim
          // dua kali. Menampilkannya sebagai galat membuat pekerjaan yang
          // sehat terlihat rusak.
          if (e.response?.status === 409 && e.response?.data?.job_id) jobId = e.response.data.job_id;
          else throw e;
        }
        if (!jobId) throw new Error('AI Service tidak mengembalikan job_id.');
        // Disimpan SEBELUM state di-set: kalau tab ditutup pada milidetik
        // berikutnya, jejaknya sudah ada di localStorage dan sesi ini bisa
        // menyambung lagi nanti.
        // `userText` ikut disimpan: setelah reload, applyAnswer tetap butuh
        // teks pertanyaan untuk estimasi token cadangan, dan closure yang dulu
        // memilikinya sudah lama hilang bersama komponen sebelumnya.
        rememberJob(sessionIdRef.current, {
          jobId, startTime, userText: sentText, modeName: modePayload?.mode || null,
        });
        setJob({ id: jobId, startTime, userText: sentText });
        // Lampiran dilepas begitu pekerjaan DITERIMA, bukan setelah selesai:
        // job sudah memegang salinannya, dan menahan lampiran di layar selama
        // menit-menit berikutnya hanya membuat user ragu kirimannya masuk.
        if (sentImgParts.length) img.clearImages();
        if (sentBlocks) textFiles.clearFiles();
        handedOff = true;
        return;   // `finally` TIDAK mematikan loading — polling yang menutupnya
      }

      const { data: r } = await api.post('/api/ai/ask', askBody);
      if (sentImgParts.length) img.clearImages();
      if (sentBlocks) textFiles.clearFiles();

      applyAnswer(r, { modePayload, startTime, userText: sentText });
    } catch (err) {
      const errMsg = `❌ ${err.response?.data?.error || err.message}`;
      setMessages(prev => [...prev, { role: 'error', content: errMsg }]);
      // Simpan pesan error ke history (dengan sedikit delay supaya tidak race dengan user msg save)
      setTimeout(() => saveChatMessage({
        session_id: sessionIdRef.current,
        dashboard:  storeName,
        // Panggilan gagal — provider yang dipakai tidak diketahui pasti.
        // Lebih baik kosong daripada mencatat tebakan yang salah.
        message_role: 'error',
        content:    errMsg,
      }), 200);
    } finally {
      if (!handedOff) {
        setLoading(false);
        // Panggilan kedua: menyegarkan jumlah pesan & waktu terakhir di sidebar
        // setelah jawaban tersimpan. reportSession stabil (useCallback deps []),
        // jadi tidak mengubah identitas sendMessage.
        reportSession();
      }
    }
  }, [input, loading, messages, config, provider, dashCtx, storeName, dataContext, isConfigured, dashboardData?.selectedYears, currentPath, dashboardKey, isCode, sqlSchema, dashCfg, img, textFiles, handleSlashCommand, reportSession, applyAnswer, rememberJob]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  // ── Edit pertanyaan ────────────────────────────────────────────────────────
  // Mulai edit: buka textarea inline berisi teks pesan user pada indeks tsb.
  const startEdit = (idx) => {
    if (loading) return;
    setEditingIdx(idx);
    setEditText(messages[idx]?.content || '');
  };
  const cancelEdit = () => { setEditingIdx(-1); setEditText(''); };
  // Submit edit (Opsi A): (1) tandai HANYA pasangan Q&A ini superseded di DB
  // (audit + token tetap terhitung), (2) hapus HANYA pesan yg di-edit + jawaban
  // langsungnya dari state — turn lain TETAP tampil, (3) kirim ulang teks baru
  // → jawaban baru muncul di bawah. Tidak lagi menghapus semua turn setelahnya.
  const submitEdit = async () => {
    const idx = editingIdx;
    const newText = editText.trim();
    if (idx < 0 || !newText || loading) return;
    const oldContent = messages[idx]?.content || '';
    setEditingIdx(-1);
    setEditText('');
    // Tandai pasangan lama superseded (backend membatasi ke pasangan ini saja).
    try {
      await api.post('/api/ai/chat-history/supersede', {
        session_id: sessionIdRef.current,
        content: oldContent,
      });
    } catch (_) { /* non-fatal: histori boleh tetap, tampilan tetap diganti */ }
    // Hapus HANYA pesan yg di-edit + jawaban langsungnya (bila ada); turn lain tetap.
    setMessages(prev => {
      const copy = [...prev];
      const removeCount = (copy[idx + 1] && copy[idx + 1].role === 'assistant') ? 2 : 1;
      copy.splice(idx, removeCount);
      return copy;
    });
    // sendMessage membaca `messages` via closure; beri jeda microtask agar state
    // terpotong dulu sebelum append pesan baru (hindari race pada follow-up detektor).
    setTimeout(() => sendMessage(newText), 0);
  };

  // Menu export ditutup saat klik di luar. Tanpa ini ia menggantung di atas
  // transkrip dan menghalangi tombol header lainnya.
  useEffect(() => {
    if (!exportOpen) return;
    const onDown = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [exportOpen]);

  // Argumen ketiga format dirakit di SATU tempat — kalau tidak, isi PDF, XLSX,
  // dan JSON bisa menyimpang satu sama lain tanpa ketahuan.
  // Model yang dilaporkan di header ekspor diambil dari giliran TERAKHIR yang
  // benar-benar dijawab, bukan dari config yang sedang aktif. Keduanya bisa
  // berbeda karena dua sebab: mode Coding meng-override provider/model, dan
  // config bisa diganti setelah percakapan berlangsung. Header yang menyebut
  // model yang tidak pernah menjawab membuat ekspor tidak bisa dipakai menilai
  // kualitas jawaban — dan itu justru alasan utama orang mengekspornya.
  const lastAnswered = () => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i] && messages[i].answeredBy) return messages[i].answeredBy;
    }
    return null;
  };

  const exportArgs = () => {
    const ab = lastAnswered();
    return {
      messages,
      dashboardName: dashCtx.name,
      // provider.name adalah label ramah ("Generic OpenAI (Custom)"); yang
      // dilaporkan server adalah kunci teknis ("openai_custom"). Dipakai apa
      // adanya karena justru kunci itu yang cocok dengan isi ai_chat_history.
      provider:      (ab && ab.provider) || provider.name,
      model:         (ab && ab.model)    || config.model,
      sessionId:     sessionIdRef.current,
    };
  };

  const handleExportXLSX = () => { setExportOpen(false); exportChatToXLSX(exportArgs()); };
  const handleExportJSON = () => { setExportOpen(false); exportChatToJSON(exportArgs()); };

  const handleExportPDF = () => {
    setExportOpen(false);
    const abPdf = lastAnswered();
    exportChatToPDF({
      messages,
      dashboardName: dashCtx.name,
      provider:      (abPdf && abPdf.provider) || provider.name,
      model:         (abPdf && abPdf.model)    || config.model,
      filters:       dashboardData?.activeFilters || dashboardData?.filters?.company || null,
    });
  };

  const handleClearChat = () => {
    setMessages([]);
    setShowPrompts(true);
    sessionIdRef.current = genSessionId(); // sesi baru
    setActiveKeyInfo({ index: 1, count: 1 });
    setProactiveToast(null);
    setServerRowCount(null);
    // Sesi lama harus berhenti disorot di sidebar begitu ditinggalkan, bukan
    // menunggu sampai pesan pertama sesi baru terkirim.
    reportSession();
  };

  if (showSettings) {
    return <AISettings config={config}
      dashboardKey={dashboardKey}
      dashboardLabel={dashCtx?.name || ''}
      dashConfig={dashCfg}
      onSave={(c) => { setConfig(c); if (onConfigChange) onConfigChange(c); setShowSettings(false); }}
      onDashSave={(dc) => setDashCfg(dc)}
      canConfigAI={canConfigAI}
      onClose={() => setShowSettings(false)} />;
  }

  // Hitung session stats
  const aiMsgs = messages.filter(m => m.role === 'assistant');
  const sessionStats = (() => {
    if (aiMsgs.length === 0) return null;
    const avgTime         = Math.round(aiMsgs.reduce((s,m) => s + (m.responseTime||0), 0) / aiMsgs.length);
    const avgScore        = Math.round(aiMsgs.reduce((s,m) => s + (calcAccuracyScore(m.content, m.hasData) || 0), 0) / aiMsgs.length);
    const totalPrompt     = aiMsgs.reduce((s,m) => s + (m.promptTokens||0), 0);
    const totalCompletion = aiMsgs.reduce((s,m) => s + (m.completionTokens||0), 0);
    const totalTokens     = aiMsgs.reduce((s,m) => s + (m.totalTokens||0), 0);
    return { avgTime, avgScore, totalPrompt, totalCompletion, totalTokens };
  })();

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* Header */}
      <div data-ai-drag="1"
        className={`flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-brand-600 to-brand-700 flex-shrink-0 ${floating ? 'cursor-move select-none' : ''}`}>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center">
            <Sparkles style={{ width: 16, height: 16 }} className="text-white" />
          </div>
          <div>
            <div className="font-semibold text-white text-sm">{t('ai.assistant')}</div>
            <div className="text-xs text-brand-200 flex items-center gap-1">
              <span>{badgeProvider.logo}</span>
              <span>{badgeProvider.name}</span>
              <span>·</span>
              <span className="truncate max-w-[120px]" title={badgeModel || ''}>{(badgeModel || '').split('-').slice(0,3).join('-')}</span>
              {badgeOverridden && (
                <span className="px-1.5 py-0.5 rounded bg-white/20 text-[10px] font-medium tracking-wide"
                  title="Provider/model khusus mode Coding (AI Configuration → Coding Mode)">
                  CODE
                </span>
              )}
              {activeKeyInfo.count > 1 && (() => {
                const budgets = getKeyBudgets(config.provider, config.apiKey);
                const cur     = budgets.find(b => b.isCurrent) || budgets[0];
                const pct     = cur?.pct ?? 100;
                const color   = pct > 50 ? '#4ade80' : pct > 20 ? '#fbbf24' : '#f87171';
                return (
                  <span className="flex items-center gap-1 bg-white/20 rounded px-1.5 py-0.5 ml-0.5"
                    title={`Key ${activeKeyInfo.index}/${activeKeyInfo.count} · Budget: ${pct}% sisa`}>
                    <Key style={{ width: 9, height: 9 }} />
                    <span>{activeKeyInfo.index}/{activeKeyInfo.count}</span>
                    {cur?.remaining !== null && (
                      <span className="flex items-center gap-0.5">
                        <span style={{ width: 24, height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 2, display:'inline-block', overflow:'hidden' }}>
                          <span style={{ display:'block', height:'100%', width: `${pct}%`, background: color, borderRadius: 2, transition:'width 0.5s' }} />
                        </span>
                        <span style={{ fontSize: 9 }}>{pct}%</span>
                      </span>
                    )}
                  </span>
                );
              })()}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {messages.filter(m=>m.role!=='error').length > 0 && (
            <div className="relative" ref={exportRef}>
              <button onClick={() => setExportOpen(v => !v)} title={t('ai.export')}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors text-white/70 hover:text-white">
                <FileDown style={{ width: 14, height: 14 }} />
              </button>
              {exportOpen && (
                <div className="absolute right-0 mt-1 z-50 min-w-[152px] rounded-lg border border-slate-200
                                bg-white shadow-lg py-1">
                  {[['PDF', handleExportPDF], ['Excel (.xlsx)', handleExportXLSX], ['JSON', handleExportJSON]]
                    .map(([label, fn]) => (
                      <button key={label} onClick={fn}
                        className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">
                        {label}
                      </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {messages.length > 0 && (
            <button onClick={handleClearChat} title={t('ai.newSession')}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors text-white/70 hover:text-white">
              <Trash2 style={{ width: 14, height: 14 }} />
            </button>
          )}
          <button onClick={() => setShowSettings(true)}
            title={canConfigAI ? t('ai.set.title') : 'Prompt'}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors text-white/70 hover:text-white">
            <Settings style={{ width: 14, height: 14 }} />
          </button>
          {onToggleFloat && !maximized && (
            <button onClick={onToggleFloat}
              title={floating ? 'Dock ke samping' : 'Undock (jendela mengapung)'}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors text-white/70 hover:text-white">
              {floating
                ? <PanelRight style={{ width: 14, height: 14 }} />
                : <PictureInPicture2 style={{ width: 14, height: 14 }} />}
            </button>
          )}
          {onToggleMaximize && (
            <button onClick={onToggleMaximize}
              title={maximized ? 'Kecilkan' : 'Perbesar 1 halaman'}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors text-white/70 hover:text-white">
              {maximized
                ? <Minimize2 style={{ width: 14, height: 14 }} />
                : <Maximize2 style={{ width: 14, height: 14 }} />}
            </button>
          )}
          <button onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors text-white/70 hover:text-white">
            <X style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>
      {/* Context bar */}
      <div className={`px-4 py-2 border-b flex items-center justify-between
        ${hasRealData ? 'bg-emerald-50 border-emerald-100' : 'bg-blue-50 border-blue-100'}`}>
        <div className="flex items-center gap-2">
          <BarChart2 style={{ width: 12, height: 12 }} className={hasRealData ? 'text-emerald-500' : 'text-blue-500'} />
          <span className={`text-xs font-medium ${hasRealData ? 'text-emerald-700' : 'text-blue-600'}`}>
            {dashCtx.name}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Database style={{ width: 10, height: 10 }} className={hasRealData ? 'text-emerald-500' : 'text-slate-300'} />
          <span className={`text-xs font-medium ${hasRealData ? 'text-emerald-600' : 'text-slate-400'}`}>
            {hasRealData
              ? `${(serverRowCount ?? dashboardData?.filtered?.length ?? dashboardData?.allData?.length ?? 0)} ${t('ai.records')}`
              : t('ai.noData')
            }
          </span>
        </div>
      </div>
      {/* Session stats bar — sekarang dengan breakdown token */}
      {sessionStats && (() => {
        const { avgTime, avgScore, totalPrompt, totalCompletion, totalTokens } = sessionStats;
        const scoreColor = avgScore >= 85 ? 'text-emerald-600' : avgScore >= 65 ? 'text-amber-600' : 'text-red-500';
        const timeColor  = avgTime < 3000 ? 'text-emerald-600' : avgTime < 8000 ? 'text-amber-600' : 'text-red-500';
        return (
          <div className="px-4 py-1.5 bg-slate-50 border-b border-slate-100 flex items-center gap-3 flex-wrap">
            <span className="text-xs text-slate-400 font-medium">{t('ai.session')}:</span>
            <span className={`text-xs font-semibold flex items-center gap-1 ${timeColor}`}>
              <Clock style={{ width: 10, height: 10 }} />
              {t('ai.avg')} {(avgTime/1000).toFixed(1)}s
            </span>
            {!config.hideAccuracy && (
            <span className={`text-xs font-semibold flex items-center gap-1 ${scoreColor}`}>
              <Target style={{ width: 10, height: 10 }} />
              {t('ai.accuracy')} ~{avgScore}%
            </span>
            )}
            <span
              className="text-xs text-brand-600 flex items-center gap-1 ml-auto font-semibold"
              title={`Prompt (input): ${totalPrompt.toLocaleString()}\nCompletion (output): ${totalCompletion.toLocaleString()}\nTotal: ${totalTokens.toLocaleString()}`}>
              <Zap style={{ width: 10, height: 10 }} />
              {totalPrompt.toLocaleString()}↑ {totalCompletion.toLocaleString()}↓ = {totalTokens.toLocaleString()} {t('ai.tokens')}
            </span>
          </div>
        );
      })()}
      {/* Not configured */}
      {!isConfigured && (
        <div className="mx-4 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-xs text-amber-700 font-medium">⚙️ {t('ai.notConfigured')}</p>
          {canConfigAI ? (
            <button onClick={() => setShowSettings(true)}
              className="mt-2 w-full py-1.5 bg-amber-500 text-white text-xs font-semibold rounded-lg hover:bg-amber-600 transition-colors">
              {t('ai.openSettings')}
            </button>
          ) : (
            <p className="mt-1 text-[11px] text-amber-600">{t('ai.notConfiguredAdmin')}</p>
          )}
        </div>
      )}
      {/* Proactive switch toast */}
      {proactiveToast && (
        <div className="mx-3 mt-2 px-3 py-1.5 bg-amber-500/90 text-white text-xs rounded-lg flex items-center gap-2 animate-pulse">
          <Zap style={{ width: 11, height: 11 }} />
          {proactiveToast}
        </div>
      )}
      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
        {messages.length === 0 && (
          <div className="text-center py-4">
            <div className="w-12 h-12 bg-gradient-to-br from-brand-500 to-brand-700 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
              <Bot style={{ width: 22, height: 22 }} className="text-white" />
            </div>
            <p className="text-slate-700 font-semibold text-sm">{t('ai.assistant')}</p>
            {hasRealData && (
              <div className="mt-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl">
                <p className="text-xs text-emerald-700 font-medium">
                  ✅ {t('ai.dataAvailable')}
                </p>
                <p className="text-xs text-emerald-600 mt-0.5">
                  {dashboardData?.filtered?.length} {t('ai.records')}
                  {dashboardData?.activeFilters ? ` · ${t('ai.filter')}: ${dashboardData.activeFilters}` : ''}
                </p>
              </div>
            )}
            <p className="text-xs text-slate-400 mt-2 flex items-center justify-center gap-1">
              <History style={{ width: 10, height: 10 }} />
              Session: <code className="text-slate-500">{sessionIdRef.current.slice(-8)}</code>
            </p>
          </div>
        )}

        {showPrompts && messages.length === 0 && isConfigured && (quickPrompts.length > 0 || canEditQuick) && (
          <div className="mt-3">
            <div className="flex items-center justify-between mb-2 px-1">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                {hasRealData ? `💡 ${t('ai.quickData')}` : t('ai.quick')}
              </p>
              {canEditQuick && customQuick !== null && (
                <button type="button" onClick={() => saveQuick(null)} disabled={qBusy}
                  title="Discard your custom list and use the built-in questions"
                  className="text-[10px] text-slate-400 hover:text-brand-600 flex items-center gap-1 disabled:opacity-50">
                  <RotateCcw style={{ width: 10, height: 10 }} /> Defaults
                </button>
              )}
            </div>
            <div className="space-y-1.5">
              {quickPrompts.map((q, i) => (
                <div key={i} className="group relative">
                  <button onClick={() => sendMessage(q)} disabled={loading}
                    className="w-full text-left px-3 py-2.5 pr-8 disabled:opacity-50 bg-white border border-slate-200 hover:border-brand-300 hover:bg-brand-50 rounded-xl text-xs text-slate-600 hover:text-brand-700 transition-all">
                    {q}
                  </button>
                  {canEditQuick && (
                    <button type="button" onClick={() => removeQuick(q)} disabled={qBusy}
                      title="Remove this quick question"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity">
                      <X style={{ width: 11, height: 11 }} />
                    </button>
                  )}
                </div>
              ))}
              {canEditQuick && (qAdding ? renderQuickAdder(false) : (
                <button type="button" onClick={() => { setQDraft(input.trim()); setQAdding(true); }}
                  className="w-full flex items-center justify-center gap-1 px-3 py-2 border border-dashed border-slate-300 hover:border-brand-300 hover:bg-brand-50 rounded-xl text-xs text-slate-400 hover:text-brand-700 transition-all">
                  <Plus style={{ width: 12, height: 12 }} /> Add quick question
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-3">
          {messages.map((msg, i) => (
            editingIdx === i ? (
              <div key={i} className="flex justify-end mb-3">
                <div className="w-[85%] bg-white border border-brand-300 rounded-2xl px-3 py-2.5 shadow-sm">
                  <textarea
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitEdit(); }
                      if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                    }}
                    autoFocus
                    rows={2}
                    className="w-full text-sm text-slate-800 outline-none resize-none"
                  />
                  <div className="flex justify-end gap-2 mt-1.5">
                    <button onClick={cancelEdit}
                      className="px-2.5 py-1 text-xs text-slate-500 hover:text-slate-700">
                      {t('common.cancel')}
                    </button>
                    <button onClick={submitEdit} disabled={!editText.trim()}
                      className="px-3 py-1 text-xs font-medium rounded-lg bg-brand-600 text-white disabled:opacity-40">
                      {t('ai.edit.resend')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Message key={i} msg={msg} hasRealData={hasRealData} isLast={i === messages.length - 1}
                hideAccuracy={!!config.hideAccuracy}
                animate={!!msg._animate}
                onAnimationDone={msg._animate ? () => setMessages(prev => prev.map((m, mi) => mi === i ? { ...m, _animate: false } : m)) : undefined}
                onStartEdit={msg.role === 'user' && !loading ? () => startEdit(i) : null}
                onOpenImage={setLightbox} />
            )
          ))}
          {(() => {
            // [FOLLOWUPS] semua dashboard (pola AI Coding), di bawah jawaban terakhir yang sukses.
            if (loading || editingIdx !== -1) return null;
            const n = messages.length;
            const last = messages[n - 1];
            if (!last || last.role !== 'assistant' || last._animate || last.error) return null;
            const ans = typeof last.content === 'string' ? last.content : '';
            if (!ans.trim() || /^(Failed|Error|⚠️)/i.test(ans.trim())) return null;
            const prevUser = [...messages].slice(0, n - 1).reverse().find(m => m.role === 'user');
            return (
              <FollowUps question={prevUser && typeof prevUser.content === 'string' ? prevUser.content : ''}
                answer={ans} lang={lang} onPick={(q) => sendMessage(q)}
                dashboard={isCode ? '' : (dashCtx?.name || '')} />
            );
          })()}
        </div>

        {loading && (
          <div className="flex gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center flex-shrink-0">
              <Bot style={{ width: 14, height: 14 }} className="text-white" />
            </div>
            <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-2">
              <Loader2 style={{ width: 14, height: 14 }} className="text-brand-500 animate-spin" />
              <span className="text-xs text-slate-400">{phaseLabel(phase, t)}</span>
              {/* Detik berjalan. Nama fase saja tidak menjawab "hidup atau
                  mati" — yang menjawab adalah apakah angkanya maju sementara
                  fasenya tidak berubah. Sumbernya elapsed_ms dari server, bukan
                  jam browser: keduanya tidak pernah sama persis. */}
              {phase && phase.elapsed_ms > 3000 && (
                <span className="text-[10px] text-slate-300 tabular-nums">
                  {Math.round(phase.elapsed_ms / 1000)}s
                </span>
              )}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      {/* Input */}
      <div className="px-4 pb-4 pt-2 border-t border-slate-100 flex-shrink-0">
        {/* Quick question chips — tetap tersedia setelah percakapan dimulai */}
        {messages.length > 0 && isConfigured && qAdding && canEditQuick && renderQuickAdder(true)}
        {messages.length > 0 && isConfigured && (quickPrompts.length > 0 || canEditQuick) && (
          <div className="flex items-center gap-1.5 mb-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
            <Zap style={{ width: 12, height: 12 }} className="text-amber-500 flex-shrink-0" title="Quick question" />
            {quickPrompts.map((q, i) => (
              <button key={i} type="button" disabled={loading}
                onClick={() => sendMessage(q)} title={q}
                className="flex-shrink-0 max-w-[220px] truncate px-2.5 py-1 rounded-full border border-slate-200 bg-white
                           text-[11px] text-slate-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700
                           disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {q}
              </button>
            ))}
            {canEditQuick && (
              <button type="button" disabled={qBusy}
                onClick={() => { if (input.trim()) addQuick(input); else { setQDraft(''); setQAdding(true); } }}
                title={input.trim() ? 'Save the text in the box as a quick question' : 'Add a quick question'}
                className="flex-shrink-0 flex items-center gap-0.5 px-2 py-1 rounded-full border border-dashed border-slate-300
                           text-[11px] text-slate-400 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700
                           disabled:opacity-50 transition-colors">
                <Plus style={{ width: 11, height: 11 }} />{input.trim() ? 'Save' : 'Add'}
              </button>
            )}
          </div>
        )}
        {(() => {
          const hasPageCtx = !!pageContext && pageContext.summary != null && pageContext.summary !== '';
          const blocked = !isGeneral && !isCode && sqlSchema?.mode !== 'sql' && !hasPageCtx;
          const blockedPh = t('aiChat.aiNotAvailablePage');
          return (
        <>
        {isCode && <ChatImageStrip img={img} t={t} />}
        {isCode && <ChatTextFileStrip files={textFiles} t={t} />}
        <div className="flex gap-2 items-end">
          {isCode && (
            <ChatAttachButton img={img} files={textFiles} t={t}
              disabled={loading || !isConfigured || blocked} />
          )}
          <textarea ref={inputRef} value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={img.onPaste}
            onDragOver={img.onDragOver}
            onDragLeave={img.onDragLeave}
            onDrop={img.onDrop}
            disabled={loading || !isConfigured || blocked}
            placeholder={blocked ? blockedPh : (isConfigured ? t('ai.placeholder') : t('ai.placeholderConfig'))}
            rows={1}
            className={`flex-1 px-3 py-2.5 rounded-xl border text-sm resize-none outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 disabled:bg-slate-50 leading-relaxed ${img.imgOver ? 'border-brand-400 ring-2 ring-brand-100 bg-brand-50/40' : 'border-slate-200'}`}
            style={{ maxHeight: 120 }}
            onInput={e => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
          />
          <button onClick={() => sendMessage()}
            disabled={(!input.trim() && !img.attachedImgs.length && !textFiles.files.length) || loading || !isConfigured || blocked}
            className="w-9 h-9 flex-shrink-0 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-200 text-white rounded-xl flex items-center justify-center transition-all">
            {loading ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" /> : <Send style={{ width: 14, height: 14 }} />}
          </button>
        </div>
        </>
          );
        })()}
        <p className="text-xs text-slate-300 mt-1.5 text-center">{t('ai.inputHint')}</p>
      </div>
      {/* Dipasang di akar komponen, bukan di dalam daftar pesan: penampil ini
          menutupi layar penuh dan tidak boleh ikut ter-clip oleh area transkrip
          yang punya overflow sendiri. */}
      <ImageLightbox image={lightbox} onClose={() => setLightbox(null)} />
      <KbSourceModal src={kbSrc} onClose={() => setKbSrc(null)} t={t} />
    </div>
  );
}
